export type FruitPollinationFruitType =
  | 'apple'
  | 'pear'
  | 'plum'
  | 'cherry'
  | 'blueberry'
  | 'peach'
  | 'mango'
  | 'pawpaw'
  | 'persimmon'
  | 'apricot'
  | 'nectarine'
  | 'mulberry'
  | 'elderberry'
  | 'fig'
  | 'avocado'
  | 'date-palm'
  | 'cherimoya'
  | 'grape'
  | 'pomegranate'
  | 'passion-fruit'
  | 'serviceberry'
  | 'mandarin'
  | 'tangerine'
  | 'kumquat'
  | 'calamondin'
  | 'citron'
  | 'quince'
  | 'kiwi'
  | 'hardy-kiwi'
  | 'gooseberry'
  | 'currant'
  | 'jujube'
  | 'honeyberry'
  | 'hazelnut'
  | 'raspberry'
  | 'blackberry'
  | 'strawberry'
  | 'aronia'
  | 'goji-berry'
  | 'cranberry'
  | 'lingonberry';

export type FruitSelfFertility =
  | 'not-self-fertile'
  | 'partially-self-fertile'
  | 'self-fertile';

export interface FruitZoneRange {
  min: number;
  max: number;
}

export type FruitPollinationRole =
  | 'fruiting'
  | 'male-pollinizer';

export type FruitChillBand =
  | 'very-low'
  | 'low'
  | 'medium'
  | 'high'
  | 'very-high';

export interface FruitPollinationVariety {
  id: string;
  name: string;
  aliases?: string[];
  fruitType: FruitPollinationFruitType;
  pollinationRole?: FruitPollinationRole;
  speciesGroup?: string;
  speciesGroupLabel?: string;
  selfFertility: FruitSelfFertility;
  bloomGroup: string;
  bloomLabel: string;
  bloomOrder: number;
  compatibilityGroup?: string;
  universalDonor?: boolean;
  pollenSterile?: boolean;
  weakPollinator?: boolean;
  zoneRange?: FruitZoneRange;
  chillHours?: string;
  chillBand?: FruitChillBand;
  compatibleVarietyIds?: string[];
  incompatibleVarietyIds?: string[];
  /** Reader-facing prose. Shown verbatim in the tool, so it must read as advice. */
  notes?: string[];
  /**
   * Sourcing and trial detail kept for authoring judgement: citations, bloom
   * offsets, cross-compatibility findings. Never rendered, because it reads as
   * annotation rather than guidance.
   */
  researchNotes?: string[];
  warnings?: string[];
}

export interface FruitPollinationTypeDefinition {
  id: FruitPollinationFruitType;
  label: string;
  description: string;
  partnerDistanceNote: string;
  minimumSetup: string;
  idealSetup: string;
  fallbackPlan: string;
  varieties: FruitPollinationVariety[];
}

export const FRUIT_POLLINATION_DATA: Record<FruitPollinationFruitType, FruitPollinationTypeDefinition> = {
  apple: {
    id: 'apple',
    label: 'Apple',
    description: 'Most apples need pollen from a different apple variety that blooms at about the same time. Even varieties that can set a few apples alone usually crop better with a partner nearby.',
    partnerDistanceNote: 'For home plantings, aim to keep partner trees within about 50 feet if possible. Nearby crabapples that bloom at the same time can also help.',
    minimumSetup: 'Plant two different apple varieties with overlapping bloom, or one apple plus a nearby ornamental crabapple that blooms at the same time.',
    idealSetup: 'Plant two to three different apples with overlapping bloom windows so each tree has more than one workable pollen source.',
    fallbackPlan: 'If you already have a poor pollinizer such as a triploid apple, add a dependable diploid apple nearby instead of counting on the existing tree to do both jobs.',
    varieties: [
      {
        id: 'crimson-crisp',
        name: 'Crimson Crisp',
        fruitType: 'apple',
        selfFertility: 'not-self-fertile',
        bloomGroup: '3',
        bloomLabel: 'early-mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 4, max: 8 },
      },
      {
        id: 'cortland',
        name: 'Cortland',
        fruitType: 'apple',
        selfFertility: 'not-self-fertile',
        bloomGroup: '3',
        bloomLabel: 'early-mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 4, max: 7 },
      },
      {
        id: 'coxs-orange-pippin',
        name: "Cox's Orange Pippin",
        aliases: ['Cox', "Cox's"],
        fruitType: 'apple',
        selfFertility: 'not-self-fertile',
        bloomGroup: '4',
        bloomLabel: 'mid bloom',
        bloomOrder: 4,
        zoneRange: { min: 5, max: 8 },
        notes: ['Usually treated as a practical mid-bloom apple, so pair it with other mid-bloom apples instead of very early or very late bloomers.'],
      },
      {
        id: 'empire',
        name: 'Empire',
        fruitType: 'apple',
        selfFertility: 'not-self-fertile',
        bloomGroup: '3',
        bloomLabel: 'early-mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 4, max: 8 },
      },
      {
        id: 'freedom',
        name: 'Freedom',
        fruitType: 'apple',
        selfFertility: 'not-self-fertile',
        bloomGroup: '3',
        bloomLabel: 'early-mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 4, max: 8 },
      },
      {
        id: 'gravenstein',
        name: 'Gravenstein',
        fruitType: 'apple',
        selfFertility: 'not-self-fertile',
        bloomGroup: '2',
        bloomLabel: 'early bloom',
        bloomOrder: 2,
        pollenSterile: true,
        zoneRange: { min: 4, max: 8 },
        warnings: [
          'Triploid. It usually cannot pollinate other apple trees.',
        ],
      },
      {
        id: 'haralson',
        name: 'Haralson',
        fruitType: 'apple',
        selfFertility: 'not-self-fertile',
        bloomGroup: '3',
        bloomLabel: 'early-mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 3, max: 6 },
      },
      {
        id: 'honeycrisp',
        name: 'Honeycrisp',
        fruitType: 'apple',
        selfFertility: 'not-self-fertile',
        bloomGroup: '4',
        bloomLabel: 'mid bloom',
        bloomOrder: 4,
        zoneRange: { min: 3, max: 7 },
      },
      {
        id: 'enterprise',
        name: 'Enterprise',
        fruitType: 'apple',
        selfFertility: 'not-self-fertile',
        bloomGroup: '4',
        bloomLabel: 'mid bloom',
        bloomOrder: 4,
        zoneRange: { min: 4, max: 8 },
      },
      {
        id: 'fuji',
        name: 'Fuji',
        fruitType: 'apple',
        selfFertility: 'partially-self-fertile',
        bloomGroup: '4',
        bloomLabel: 'mid bloom',
        bloomOrder: 4,
        zoneRange: { min: 5, max: 8 },
        notes: ['May set a light crop alone, but a partner usually improves fruit set and yield.'],
      },
      {
        id: 'gala',
        name: 'Gala',
        fruitType: 'apple',
        selfFertility: 'not-self-fertile',
        bloomGroup: '4',
        bloomLabel: 'mid bloom',
        bloomOrder: 4,
        zoneRange: { min: 5, max: 8 },
      },
      {
        id: 'golden-delicious',
        name: 'Golden Delicious',
        fruitType: 'apple',
        selfFertility: 'partially-self-fertile',
        bloomGroup: '4',
        bloomLabel: 'mid bloom',
        bloomOrder: 4,
        zoneRange: { min: 5, max: 8 },
        notes: ['Often used as a good pollinizing partner because it blooms broadly and produces workable pollen.'],
      },
      {
        id: 'goldrush',
        name: 'GoldRush',
        fruitType: 'apple',
        selfFertility: 'not-self-fertile',
        bloomGroup: '4',
        bloomLabel: 'mid bloom',
        bloomOrder: 4,
        zoneRange: { min: 5, max: 8 },
        notes: ['Disease-resistant home orchard apple that is usually handled as a practical mid-bloom pollination partner.'],
      },
      {
        id: 'granny-smith',
        name: 'Granny Smith',
        fruitType: 'apple',
        selfFertility: 'partially-self-fertile',
        bloomGroup: '4',
        bloomLabel: 'mid bloom',
        bloomOrder: 4,
        zoneRange: { min: 5, max: 9 },
      },
      {
        id: 'arkansas-black',
        name: 'Arkansas Black',
        fruitType: 'apple',
        selfFertility: 'not-self-fertile',
        bloomGroup: '4',
        bloomLabel: 'mid bloom',
        bloomOrder: 4,
        zoneRange: { min: 5, max: 8 },
        notes: ['Usually handled as a mid-bloom apple in home orchard charts, so another mid-bloom apple is the safer partner choice.'],
      },
      {
        id: 'jonagold',
        name: 'Jonagold',
        fruitType: 'apple',
        selfFertility: 'not-self-fertile',
        bloomGroup: '4',
        bloomLabel: 'mid bloom',
        bloomOrder: 4,
        pollenSterile: true,
        zoneRange: { min: 5, max: 8 },
        warnings: [
          'Triploid. It needs another apple nearby, but it usually cannot return the favor as a pollinizer.',
        ],
      },
      {
        id: 'liberty',
        name: 'Liberty',
        fruitType: 'apple',
        selfFertility: 'not-self-fertile',
        bloomGroup: '3',
        bloomLabel: 'early-mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 4, max: 8 },
      },
      {
        id: 'mcintosh',
        name: 'McIntosh',
        fruitType: 'apple',
        selfFertility: 'not-self-fertile',
        bloomGroup: '3',
        bloomLabel: 'early-mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 4, max: 7 },
      },
      {
        id: 'ashmeads-kernel',
        name: "Ashmead's Kernel",
        fruitType: 'apple',
        selfFertility: 'not-self-fertile',
        bloomGroup: '4',
        bloomLabel: 'mid bloom',
        bloomOrder: 4,
        zoneRange: { min: 4, max: 8 },
        notes: ['Popular heritage apple that is usually safest with another mid-bloom apple nearby.'],
      },
      {
        id: 'mutsu-crispin',
        name: 'Mutsu / Crispin',
        fruitType: 'apple',
        selfFertility: 'not-self-fertile',
        bloomGroup: '4',
        bloomLabel: 'mid bloom',
        bloomOrder: 4,
        pollenSterile: true,
        zoneRange: { min: 5, max: 8 },
        warnings: [
          'Triploid. It is usually a poor pollinizer for other apples.',
        ],
      },
      {
        id: 'pink-lady',
        name: 'Pink Lady',
        fruitType: 'apple',
        selfFertility: 'not-self-fertile',
        bloomGroup: '5',
        bloomLabel: 'mid-late bloom',
        bloomOrder: 5,
        zoneRange: { min: 6, max: 9 },
      },
      {
        id: 'pristine',
        name: 'Pristine',
        fruitType: 'apple',
        selfFertility: 'not-self-fertile',
        bloomGroup: '2',
        bloomLabel: 'early bloom',
        bloomOrder: 2,
        zoneRange: { min: 4, max: 8 },
        notes: ['Useful early disease-resistant apple. Pair it with another early to early-mid bloomer.'],
      },
      {
        id: 'bramley-seedling',
        name: 'Bramley Seedling',
        fruitType: 'apple',
        selfFertility: 'not-self-fertile',
        bloomGroup: '4',
        bloomLabel: 'mid bloom',
        bloomOrder: 4,
        pollenSterile: true,
        zoneRange: { min: 5, max: 8 },
        warnings: ['Triploid. This classic cooking apple usually needs help from other apples, but it is not a dependable pollen source itself.'],
      },
      {
        id: 'red-delicious',
        name: 'Red Delicious',
        fruitType: 'apple',
        selfFertility: 'not-self-fertile',
        bloomGroup: '4',
        bloomLabel: 'mid bloom',
        bloomOrder: 4,
        zoneRange: { min: 5, max: 8 },
      },
      {
        id: 'rome-beauty',
        name: 'Rome Beauty',
        fruitType: 'apple',
        selfFertility: 'not-self-fertile',
        bloomGroup: '5',
        bloomLabel: 'mid-late bloom',
        bloomOrder: 5,
        zoneRange: { min: 5, max: 8 },
      },
    ],
  },
  pear: {
    id: 'pear',
    label: 'Pear',
    description: 'Most pears crop better with a second variety nearby. Some can set a modest crop alone, but a partner usually improves reliability and helps fill out the fruit.',
    partnerDistanceNote: 'Try to keep partner trees within about 50 feet when possible. Pear bloom can be damaged by late frost, so a partner adds insurance.',
    minimumSetup: 'Plant two pear varieties with overlapping bloom whenever you can, even if one is partly self-fruitful.',
    idealSetup: 'Plant two to three pears with similar bloom timing so cool spring weather or weak bee activity does not leave a tree without pollen.',
    fallbackPlan: 'If space is tight, keep one productive pear and add one dependable pollinizing partner rather than relying on a single tree to crop heavily every year.',
    varieties: [
      {
        id: 'bartlett',
        name: 'Bartlett',
        fruitType: 'pear',
        speciesGroup: 'european',
        speciesGroupLabel: 'European pear',
        selfFertility: 'partially-self-fertile',
        bloomGroup: '3',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 8 },
      },
      {
        id: 'bosc',
        name: 'Bosc',
        fruitType: 'pear',
        speciesGroup: 'european',
        speciesGroupLabel: 'European pear',
        selfFertility: 'not-self-fertile',
        bloomGroup: '3',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 8 },
      },
      {
        id: 'comice',
        name: 'Comice',
        fruitType: 'pear',
        speciesGroup: 'european',
        speciesGroupLabel: 'European pear',
        selfFertility: 'not-self-fertile',
        bloomGroup: '3',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 8 },
      },
      {
        id: 'd-anjou',
        name: "Anjou / D'Anjou",
        aliases: ['Anjou', "D'Anjou", 'D Anjou'],
        fruitType: 'pear',
        speciesGroup: 'european',
        speciesGroupLabel: 'European pear',
        selfFertility: 'not-self-fertile',
        bloomGroup: '3',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 8 },
      },
      {
        id: 'golden-spice',
        name: 'Golden Spice',
        fruitType: 'pear',
        speciesGroup: 'european',
        speciesGroupLabel: 'European pear',
        selfFertility: 'not-self-fertile',
        bloomGroup: '2',
        bloomLabel: 'early bloom',
        bloomOrder: 2,
        zoneRange: { min: 3, max: 5 },
        notes: ['Often grown mainly as a hardy pollinizing pear in colder regions.'],
      },
      {
        id: 'gourmet',
        name: 'Gourmet',
        fruitType: 'pear',
        speciesGroup: 'european',
        speciesGroupLabel: 'European pear',
        selfFertility: 'partially-self-fertile',
        bloomGroup: '3',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        pollenSterile: true,
        zoneRange: { min: 4, max: 6 },
        warnings: ['This variety usually cannot serve as the pollen source for another pear.'],
      },
      {
        id: 'hosui',
        name: 'Hosui',
        fruitType: 'pear',
        speciesGroup: 'asian',
        speciesGroupLabel: 'Asian pear',
        selfFertility: 'not-self-fertile',
        bloomGroup: '3',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 9 },
      },
      {
        id: 'chojuro',
        name: 'Chojuro',
        fruitType: 'pear',
        speciesGroup: 'asian',
        speciesGroupLabel: 'Asian pear',
        selfFertility: 'partially-self-fertile',
        bloomGroup: '3',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 9 },
        notes: ['Can set some fruit alone, but another Asian pear is the safer choice for heavier crops.'],
      },
      {
        id: 'juicy-jewel',
        name: 'Juicy Jewel',
        fruitType: 'pear',
        speciesGroup: 'asian',
        speciesGroupLabel: 'Asian pear',
        selfFertility: 'not-self-fertile',
        bloomGroup: '2',
        bloomLabel: 'early bloom',
        bloomOrder: 2,
        zoneRange: { min: 4, max: 5 },
        compatibleVarietyIds: ['summercrisp', 'shinseiki', 'twentieth-century'],
        researchNotes: ['University of Minnesota guidance specifically notes that Juicy Jewel and Summercrisp can pollinate one another.'],
      },
      {
        id: 'luscious',
        name: 'Luscious',
        fruitType: 'pear',
        speciesGroup: 'european',
        speciesGroupLabel: 'European pear',
        selfFertility: 'partially-self-fertile',
        bloomGroup: '3',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        pollenSterile: true,
        zoneRange: { min: 4, max: 6 },
        warnings: ['This variety can set some fruit, but it usually cannot provide useful pollen to another pear tree.'],
      },
      {
        id: 'moonglow',
        name: 'Moonglow',
        fruitType: 'pear',
        speciesGroup: 'european',
        speciesGroupLabel: 'European pear',
        selfFertility: 'not-self-fertile',
        bloomGroup: '3',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 8 },
      },
      {
        id: 'olympic',
        name: 'Olympic / Korean Giant',
        fruitType: 'pear',
        speciesGroup: 'asian',
        speciesGroupLabel: 'Asian pear',
        selfFertility: 'not-self-fertile',
        bloomGroup: '4',
        bloomLabel: 'mid-late bloom',
        bloomOrder: 4,
        zoneRange: { min: 5, max: 9 },
        notes: ['Penn State notes that its bloom often overlaps Bartlett, so it may work in mixed pear plantings where bloom timing lines up.'],
      },
      {
        id: 'parker',
        name: 'Parker',
        fruitType: 'pear',
        speciesGroup: 'european',
        speciesGroupLabel: 'European pear',
        selfFertility: 'partially-self-fertile',
        bloomGroup: '2',
        bloomLabel: 'early bloom',
        bloomOrder: 2,
        zoneRange: { min: 4, max: 6 },
        researchNotes: ['University of Minnesota notes that Parker is a good pollinizer for Luscious.'],
      },
      {
        id: 'patten',
        name: 'Patten',
        fruitType: 'pear',
        speciesGroup: 'european',
        speciesGroupLabel: 'European pear',
        selfFertility: 'partially-self-fertile',
        bloomGroup: '3',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 4, max: 6 },
      },
      {
        id: 'potomac',
        name: 'Potomac',
        fruitType: 'pear',
        speciesGroup: 'european',
        speciesGroupLabel: 'European pear',
        selfFertility: 'not-self-fertile',
        bloomGroup: '3',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 8 },
      },
      {
        id: 'seckel',
        name: 'Seckel',
        fruitType: 'pear',
        speciesGroup: 'european',
        speciesGroupLabel: 'European pear',
        selfFertility: 'partially-self-fertile',
        bloomGroup: '3',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 8 },
      },
      {
        id: 'kieffer',
        name: 'Kieffer',
        fruitType: 'pear',
        speciesGroup: 'european',
        speciesGroupLabel: 'European pear',
        selfFertility: 'partially-self-fertile',
        bloomGroup: '3',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 4, max: 9 },
        notes: ['Common old home-orchard pear. It can set some fruit alone, but another pear usually improves consistency.'],
      },
      {
        id: 'shinko',
        name: 'Shinko',
        fruitType: 'pear',
        speciesGroup: 'asian',
        speciesGroupLabel: 'Asian pear',
        selfFertility: 'partially-self-fertile',
        bloomGroup: '3',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 9 },
        notes: ['Usually treated as partly self-fruitful, but another Asian pear still improves set.'],
      },
      {
        id: 'shinseiki',
        name: 'Shinseiki / 20th Century Type',
        fruitType: 'pear',
        speciesGroup: 'asian',
        speciesGroupLabel: 'Asian pear',
        selfFertility: 'partially-self-fertile',
        bloomGroup: '2',
        bloomLabel: 'early bloom',
        bloomOrder: 2,
        zoneRange: { min: 5, max: 9 },
        notes: ['Penn State notes that Shinseiki can set fruit by itself, but still benefits from cross-pollination.'],
      },
      {
        id: 'summercrisp',
        name: 'Summercrisp',
        fruitType: 'pear',
        speciesGroup: 'european',
        speciesGroupLabel: 'European pear',
        selfFertility: 'not-self-fertile',
        bloomGroup: '2',
        bloomLabel: 'early bloom',
        bloomOrder: 2,
        zoneRange: { min: 4, max: 6 },
        compatibleVarietyIds: ['juicy-jewel', 'golden-spice', 'parker', 'ure'],
        researchNotes: ['University of Minnesota specifically notes that Summercrisp and Juicy Jewel can pollinate one another.'],
      },
      {
        id: 'twentieth-century',
        name: 'Twentieth Century / Nijisseiki',
        fruitType: 'pear',
        speciesGroup: 'asian',
        speciesGroupLabel: 'Asian pear',
        selfFertility: 'not-self-fertile',
        bloomGroup: '2',
        bloomLabel: 'early bloom',
        bloomOrder: 2,
        zoneRange: { min: 5, max: 9 },
      },
      {
        id: 'ure',
        name: 'Ure',
        fruitType: 'pear',
        speciesGroup: 'european',
        speciesGroupLabel: 'European pear',
        selfFertility: 'not-self-fertile',
        bloomGroup: '2',
        bloomLabel: 'early bloom',
        bloomOrder: 2,
        zoneRange: { min: 3, max: 5 },
        compatibleVarietyIds: ['golden-spice', 'summercrisp', 'parker'],
        researchNotes: ['University of Minnesota especially recommends Golden Spice as a pollen source for Ure in colder regions.'],
      },
    ],
  },
  plum: {
    id: 'plum',
    label: 'Plum',
    description: 'Plums are where pollination gets trickier. European plums usually need European partners, Japanese plums usually need Japanese partners, and hardy native or hybrid plums often need another hardy hybrid or native-type partner.',
    partnerDistanceNote: 'Keep partner trees within about 50 feet if you can. Plum bloom is early, so having a well-matched partner improves the odds in cool spring weather.',
    minimumSetup: 'Plant two compatible plums from the same pollination group, such as European with European or Japanese with Japanese.',
    idealSetup: 'Use two or three plums in the same group with overlapping bloom so frost, poor weather, or shy bee activity does not ruin fruit set.',
    fallbackPlan: 'If you only have room for one plum, choose a self-fruitful European type, but still plan for a second compatible plum later if yield matters.',
    varieties: [
      {
        id: 'castleton',
        name: 'Castleton',
        fruitType: 'plum',
        speciesGroup: 'european',
        speciesGroupLabel: 'European plum',
        selfFertility: 'self-fertile',
        bloomGroup: '3',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 8 },
      },
      {
        id: 'damson',
        name: 'Damson',
        fruitType: 'plum',
        speciesGroup: 'european',
        speciesGroupLabel: 'European plum',
        selfFertility: 'self-fertile',
        bloomGroup: '3',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 8 },
      },
      {
        id: 'green-gage',
        name: 'Green Gage',
        fruitType: 'plum',
        speciesGroup: 'european',
        speciesGroupLabel: 'European plum',
        selfFertility: 'self-fertile',
        bloomGroup: '3',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 8 },
      },
      {
        id: 'italian-prune',
        name: 'Italian Prune / Fellenberg',
        fruitType: 'plum',
        speciesGroup: 'european',
        speciesGroupLabel: 'European plum',
        selfFertility: 'self-fertile',
        bloomGroup: '3',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 8 },
      },
      {
        id: 'mount-royal',
        name: 'Mount Royal',
        fruitType: 'plum',
        speciesGroup: 'european',
        speciesGroupLabel: 'European plum',
        selfFertility: 'self-fertile',
        bloomGroup: '3',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 4, max: 7 },
      },
      {
        id: 'stanley',
        name: 'Stanley',
        fruitType: 'plum',
        speciesGroup: 'european',
        speciesGroupLabel: 'European plum',
        selfFertility: 'self-fertile',
        bloomGroup: '3',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 8 },
      },
      {
        id: 'beauty',
        name: 'Beauty',
        fruitType: 'plum',
        speciesGroup: 'japanese',
        speciesGroupLabel: 'Japanese plum',
        selfFertility: 'not-self-fertile',
        bloomGroup: '2',
        bloomLabel: 'early bloom',
        bloomOrder: 2,
        zoneRange: { min: 6, max: 9 },
      },
      {
        id: 'burgundy',
        name: 'Burgundy',
        fruitType: 'plum',
        speciesGroup: 'japanese',
        speciesGroupLabel: 'Japanese plum',
        selfFertility: 'partially-self-fertile',
        bloomGroup: '3',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 6, max: 9 },
      },
      {
        id: 'elephant-heart',
        name: 'Elephant Heart',
        fruitType: 'plum',
        speciesGroup: 'japanese',
        speciesGroupLabel: 'Japanese plum',
        selfFertility: 'not-self-fertile',
        bloomGroup: '3',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 6, max: 9 },
      },
      {
        id: 'hollywood',
        name: 'Hollywood',
        fruitType: 'plum',
        speciesGroup: 'japanese',
        speciesGroupLabel: 'Japanese plum',
        selfFertility: 'partially-self-fertile',
        bloomGroup: '2',
        bloomLabel: 'early bloom',
        bloomOrder: 2,
        zoneRange: { min: 5, max: 9 },
      },
      {
        id: 'methley',
        name: 'Methley',
        fruitType: 'plum',
        speciesGroup: 'japanese',
        speciesGroupLabel: 'Japanese plum',
        selfFertility: 'partially-self-fertile',
        bloomGroup: '2',
        bloomLabel: 'early bloom',
        bloomOrder: 2,
        zoneRange: { min: 5, max: 9 },
      },
      {
        id: 'santa-rosa',
        name: 'Santa Rosa',
        fruitType: 'plum',
        speciesGroup: 'japanese',
        speciesGroupLabel: 'Japanese plum',
        selfFertility: 'partially-self-fertile',
        bloomGroup: '3',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 9 },
      },
      {
        id: 'satsuma',
        name: 'Satsuma',
        fruitType: 'plum',
        speciesGroup: 'japanese',
        speciesGroupLabel: 'Japanese plum',
        selfFertility: 'not-self-fertile',
        bloomGroup: '3',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 6, max: 9 },
      },
      {
        id: 'shiro',
        name: 'Shiro',
        fruitType: 'plum',
        speciesGroup: 'japanese',
        speciesGroupLabel: 'Japanese plum',
        selfFertility: 'partially-self-fertile',
        bloomGroup: '2',
        bloomLabel: 'early bloom',
        bloomOrder: 2,
        zoneRange: { min: 5, max: 9 },
      },
      {
        id: 'alderman',
        name: 'Alderman',
        fruitType: 'plum',
        speciesGroup: 'hybrid',
        speciesGroupLabel: 'Hardy hybrid plum',
        selfFertility: 'not-self-fertile',
        bloomGroup: '2',
        bloomLabel: 'early bloom',
        bloomOrder: 2,
        zoneRange: { min: 3, max: 6 },
        compatibleVarietyIds: ['superior', 'toka', 'waneta', 'pembina'],
      },
      {
        id: 'blackice',
        name: 'BlackIce',
        fruitType: 'plum',
        speciesGroup: 'hybrid',
        speciesGroupLabel: 'Hardy hybrid plum',
        selfFertility: 'not-self-fertile',
        bloomGroup: '2',
        bloomLabel: 'early bloom',
        bloomOrder: 2,
        zoneRange: { min: 3, max: 6 },
        compatibleVarietyIds: ['toka', 'waneta', 'pembina', 'superior'],
      },
      {
        id: 'la-crescent',
        name: 'La Crescent',
        fruitType: 'plum',
        speciesGroup: 'hybrid',
        speciesGroupLabel: 'Hardy hybrid plum',
        selfFertility: 'not-self-fertile',
        bloomGroup: '2',
        bloomLabel: 'early bloom',
        bloomOrder: 2,
        zoneRange: { min: 3, max: 6 },
        compatibleVarietyIds: ['toka', 'waneta', 'pembina', 'blackice'],
        incompatibleVarietyIds: ['superior'],
        warnings: ['University of Minnesota guidance specifically notes that La Crescent should not rely on Superior as its partner.'],
      },
      {
        id: 'pembina',
        name: 'Pembina',
        fruitType: 'plum',
        speciesGroup: 'hybrid',
        speciesGroupLabel: 'Hardy hybrid plum',
        selfFertility: 'not-self-fertile',
        bloomGroup: '2',
        bloomLabel: 'early bloom',
        bloomOrder: 2,
        zoneRange: { min: 3, max: 6 },
        compatibleVarietyIds: ['toka', 'waneta', 'superior', 'alderman', 'blackice', 'la-crescent'],
      },
      {
        id: 'superior',
        name: 'Superior',
        fruitType: 'plum',
        speciesGroup: 'hybrid',
        speciesGroupLabel: 'Hardy hybrid plum',
        selfFertility: 'not-self-fertile',
        bloomGroup: '2',
        bloomLabel: 'early bloom',
        bloomOrder: 2,
        zoneRange: { min: 3, max: 6 },
        compatibleVarietyIds: ['toka', 'waneta', 'alderman', 'pembina', 'blackice'],
      },
      {
        id: 'toka',
        name: 'Toka',
        fruitType: 'plum',
        speciesGroup: 'hybrid',
        speciesGroupLabel: 'Hardy hybrid plum',
        selfFertility: 'not-self-fertile',
        bloomGroup: '2',
        bloomLabel: 'early bloom',
        bloomOrder: 2,
        zoneRange: { min: 3, max: 6 },
        compatibleVarietyIds: ['alderman', 'blackice', 'la-crescent', 'pembina', 'superior', 'waneta'],
        notes: ['Often recommended as one of the most useful hardy hybrid plum pollinizers.'],
      },
      {
        id: 'waneta',
        name: 'Waneta',
        fruitType: 'plum',
        speciesGroup: 'hybrid',
        speciesGroupLabel: 'Hardy hybrid plum',
        selfFertility: 'not-self-fertile',
        bloomGroup: '2',
        bloomLabel: 'early bloom',
        bloomOrder: 2,
        zoneRange: { min: 3, max: 6 },
        compatibleVarietyIds: ['toka', 'superior', 'pembina', 'alderman', 'blackice', 'la-crescent'],
      },
    ],
  },
  cherry: {
    id: 'cherry',
    label: 'Cherry',
    description: 'Tart cherries are usually the easy ones: they are self-fertile, though another tart cherry can still improve fruit set. Sweet cherries are more demanding and often need a specific sweet cherry partner.',
    partnerDistanceNote: 'Sweet cherry pollination is most dependable when trees are within about 50 feet. Tart and sweet cherries should not be treated as interchangeable pollinizing partners.',
    minimumSetup: 'For tart cherries, one tree can work. For sweet cherries, start with either one self-fertile sweet cherry or two compatible sweet cherry varieties.',
    idealSetup: 'Grow two to three compatible sweet cherries or a self-fertile sweet cherry plus another matched variety for better fruit set and backup.',
    fallbackPlan: 'If you already planted an incompatible sweet cherry, add a compatible sweet cherry instead of hoping a tart cherry will fill the gap.',
    varieties: [
      {
        id: 'balaton',
        name: 'Balaton',
        fruitType: 'cherry',
        speciesGroup: 'tart',
        speciesGroupLabel: 'Tart cherry',
        selfFertility: 'self-fertile',
        bloomGroup: '3',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 4, max: 8 },
      },
      {
        id: 'danube',
        name: 'Danube',
        fruitType: 'cherry',
        speciesGroup: 'tart',
        speciesGroupLabel: 'Tart cherry',
        selfFertility: 'self-fertile',
        bloomGroup: '3',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 4, max: 8 },
      },
      {
        id: 'meteor',
        name: 'Meteor',
        fruitType: 'cherry',
        speciesGroup: 'tart',
        speciesGroupLabel: 'Tart cherry',
        selfFertility: 'self-fertile',
        bloomGroup: '3',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 4, max: 7 },
      },
      {
        id: 'montmorency',
        name: 'Montmorency',
        fruitType: 'cherry',
        speciesGroup: 'tart',
        speciesGroupLabel: 'Tart cherry',
        selfFertility: 'self-fertile',
        bloomGroup: '3',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 4, max: 8 },
      },
      {
        id: 'north-star',
        name: 'North Star',
        fruitType: 'cherry',
        speciesGroup: 'tart',
        speciesGroupLabel: 'Tart cherry',
        selfFertility: 'self-fertile',
        bloomGroup: '3',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 4, max: 8 },
      },
      {
        id: 'jubileum',
        name: 'Jubileum',
        fruitType: 'cherry',
        speciesGroup: 'tart',
        speciesGroupLabel: 'Tart cherry',
        selfFertility: 'self-fertile',
        bloomGroup: '3',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 4, max: 8 },
        notes: ['Bloom timing is handled conservatively as mid bloom here, which keeps it paired with the tart cherry group unless a nursery gives a more specific local chart.'],
      },
      {
        id: 'evans-bali',
        name: 'Evans / Bali',
        aliases: ['Evans Bali', 'Bali', 'Evans'],
        fruitType: 'cherry',
        speciesGroup: 'tart',
        speciesGroupLabel: 'Tart cherry',
        selfFertility: 'self-fertile',
        bloomGroup: '3',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 3, max: 7 },
        notes: ['Also sold simply as Bali. Treated here as a tart cherry for partner matching, so it stays inside the tart group.'],
      },
      {
        id: 'mesabi',
        name: 'Mesabi',
        fruitType: 'cherry',
        speciesGroup: 'tart',
        speciesGroupLabel: 'Tart cherry hybrid',
        selfFertility: 'self-fertile',
        bloomGroup: '3',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 4, max: 7 },
        notes: ['Handled conservatively with tart cherries for home pollination planning, even though nursery descriptions often call it a tart cherry hybrid.'],
      },
      {
        id: 'surefire',
        name: 'Surefire',
        fruitType: 'cherry',
        speciesGroup: 'tart',
        speciesGroupLabel: 'Tart cherry',
        selfFertility: 'self-fertile',
        bloomGroup: '4',
        bloomLabel: 'mid-late bloom',
        bloomOrder: 4,
        zoneRange: { min: 4, max: 8 },
      },
      {
        id: 'bing',
        name: 'Bing',
        fruitType: 'cherry',
        speciesGroup: 'sweet',
        speciesGroupLabel: 'Sweet cherry',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'III',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        compatibilityGroup: 'III',
        zoneRange: { min: 5, max: 8 },
      },
      {
        id: 'black-tartarian',
        name: 'Black Tartarian',
        fruitType: 'cherry',
        speciesGroup: 'sweet',
        speciesGroupLabel: 'Sweet cherry',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'I',
        bloomLabel: 'early-mid bloom',
        bloomOrder: 2,
        compatibilityGroup: 'I',
        zoneRange: { min: 5, max: 8 },
      },
      {
        id: 'benton',
        name: 'Benton',
        fruitType: 'cherry',
        speciesGroup: 'sweet',
        speciesGroupLabel: 'Sweet cherry',
        selfFertility: 'self-fertile',
        bloomGroup: "S4'",
        bloomLabel: 'late bloom',
        bloomOrder: 5,
        zoneRange: { min: 5, max: 8 },
        universalDonor: true,
        researchNotes: ['Self-fertile sweet cherry; WSU confirms late bloom, 4-5 days after Bing; flexible pollinizing partner for all sweet cherry compatibility groups'],
      },
      {
        id: 'blackgold',
        name: 'BlackGold',
        fruitType: 'cherry',
        speciesGroup: 'sweet',
        speciesGroupLabel: 'Sweet cherry',
        selfFertility: 'self-fertile',
        bloomGroup: "S4'",
        bloomLabel: 'late bloom',
        bloomOrder: 5,
        zoneRange: { min: 4, max: 8 },
        universalDonor: true,
        researchNotes: ['Cornell Geneva self-fertile sweet cherry; late bloom confirmed by multiple sources; cold-hardy to zone 4; practical pollinizing partner for colder sweet cherry plantings'],
      },
      {
        id: 'hudson',
        name: 'Hudson',
        fruitType: 'cherry',
        speciesGroup: 'sweet',
        speciesGroupLabel: 'Sweet cherry',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'IX',
        bloomLabel: 'late bloom',
        bloomOrder: 5,
        compatibilityGroup: 'IX',
        zoneRange: { min: 5, max: 8 },
      },
      {
        id: 'lapins',
        name: 'Lapins',
        fruitType: 'cherry',
        speciesGroup: 'sweet',
        speciesGroupLabel: 'Sweet cherry',
        selfFertility: 'self-fertile',
        bloomGroup: "S4'",
        bloomLabel: 'early-mid bloom',
        bloomOrder: 2,
        zoneRange: { min: 5, max: 8 },
        universalDonor: true,
        researchNotes: ['Self-fertile PARC Summerland release; WSU confirms early bloom, 2-4 days before Bing; widely used as a practical sweet cherry pollinizer'],
      },
      {
        id: 'rainier',
        name: 'Rainier',
        fruitType: 'cherry',
        speciesGroup: 'sweet',
        speciesGroupLabel: 'Sweet cherry',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'IX',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        compatibilityGroup: 'IX',
        zoneRange: { min: 5, max: 8 },
      },
      {
        id: 'regina',
        name: 'Regina',
        fruitType: 'cherry',
        speciesGroup: 'sweet',
        speciesGroupLabel: 'Sweet cherry',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'II',
        bloomLabel: 'late bloom',
        bloomOrder: 5,
        compatibilityGroup: 'II',
        zoneRange: { min: 5, max: 8 },
      },
      {
        id: 'sandra-rose',
        name: 'Sandra Rose',
        fruitType: 'cherry',
        speciesGroup: 'sweet',
        speciesGroupLabel: 'Sweet cherry',
        selfFertility: 'self-fertile',
        bloomGroup: "S4'",
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 8 },
        universalDonor: true,
        researchNotes: ['Self-fertile sweet cherry carrying S4\'; confirmed in OSU EM-9484 sweet cherry compatibility chart; mid bloom; can pollinate any compatible sweet cherry variety'],
      },
      {
        id: 'sam',
        name: 'Sam',
        fruitType: 'cherry',
        speciesGroup: 'sweet',
        speciesGroupLabel: 'Sweet cherry',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'XIII',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        compatibilityGroup: 'XIII',
        zoneRange: { min: 5, max: 8 },
      },
      {
        id: 'utah-giant',
        name: 'Utah Giant',
        fruitType: 'cherry',
        speciesGroup: 'sweet',
        speciesGroupLabel: 'Sweet cherry',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'III',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 8 },
        notes: ['Handled conservatively as a mid-bloom sweet cherry. Another sweet cherry is still required; tart cherries are not a substitute.'],
      },
      {
        id: 'skeena',
        name: 'Skeena',
        fruitType: 'cherry',
        speciesGroup: 'sweet',
        speciesGroupLabel: 'Sweet cherry',
        selfFertility: 'self-fertile',
        bloomGroup: "S4'",
        bloomLabel: 'late bloom',
        bloomOrder: 5,
        zoneRange: { min: 5, max: 8 },
        universalDonor: true,
      },
      {
        id: 'stella',
        name: 'Stella',
        fruitType: 'cherry',
        speciesGroup: 'sweet',
        speciesGroupLabel: 'Sweet cherry',
        selfFertility: 'self-fertile',
        bloomGroup: "S4'",
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 8 },
        universalDonor: true,
      },
      {
        id: 'whitegold',
        name: 'WhiteGold',
        fruitType: 'cherry',
        speciesGroup: 'sweet',
        speciesGroupLabel: 'Sweet cherry',
        selfFertility: 'self-fertile',
        bloomGroup: "S4'",
        bloomLabel: 'early-mid bloom',
        bloomOrder: 2,
        zoneRange: { min: 5, max: 8 },
        universalDonor: true,
      },
      {
        id: 'sweetheart',
        name: 'Sweetheart',
        fruitType: 'cherry',
        speciesGroup: 'sweet',
        speciesGroupLabel: 'Sweet cherry',
        selfFertility: 'self-fertile',
        bloomGroup: "S4'",
        bloomLabel: 'early bloom',
        bloomOrder: 1,
        zoneRange: { min: 5, max: 8 },
        universalDonor: true,
      },
      {
        id: 'van',
        name: 'Van',
        fruitType: 'cherry',
        speciesGroup: 'sweet',
        speciesGroupLabel: 'Sweet cherry',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'II',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        compatibilityGroup: 'II',
        zoneRange: { min: 5, max: 8 },
      },
    ],
  },
  blueberry: {
    id: 'blueberry',
    label: 'Blueberry',
    description: 'Blueberries usually reward you for planting more than one variety. Even self-fertile highbush blueberries tend to make larger, better-filled berries when a compatible partner blooms nearby.',
    partnerDistanceNote: 'Blueberry partners work best when planted within the same bed or in nearby rows so bees can easily move between them.',
    minimumSetup: 'Plant at least two blueberry varieties from the same blueberry type, such as two northern highbush or two rabbiteye varieties.',
    idealSetup: 'Use two to four blueberry varieties from the same type with staggered but overlapping bloom so harvest is longer and fruit set stays strong.',
    fallbackPlan: 'If you already have a lone blueberry, add one compatible variety from the same blueberry type rather than mixing northern highbush with rabbiteye and hoping for the best.',
    varieties: [
      {
        id: 'duke',
        name: 'Duke',
        fruitType: 'blueberry',
        speciesGroup: 'northern-highbush',
        speciesGroupLabel: 'Northern highbush',
        selfFertility: 'self-fertile',
        bloomGroup: 'early',
        bloomLabel: 'early bloom',
        bloomOrder: 2,
        zoneRange: { min: 4, max: 8 },
      },
      {
        id: 'patriot',
        name: 'Patriot',
        fruitType: 'blueberry',
        speciesGroup: 'northern-highbush',
        speciesGroupLabel: 'Northern highbush',
        selfFertility: 'self-fertile',
        bloomGroup: 'early',
        bloomLabel: 'early bloom',
        bloomOrder: 2,
        zoneRange: { min: 3, max: 7 },
      },
      {
        id: 'spartan',
        name: 'Spartan',
        fruitType: 'blueberry',
        speciesGroup: 'northern-highbush',
        speciesGroupLabel: 'Northern highbush',
        selfFertility: 'self-fertile',
        bloomGroup: 'early',
        bloomLabel: 'early bloom',
        bloomOrder: 2,
        zoneRange: { min: 4, max: 7 },
      },
      {
        id: 'bluecrop',
        name: 'Bluecrop',
        fruitType: 'blueberry',
        speciesGroup: 'northern-highbush',
        speciesGroupLabel: 'Northern highbush',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 4, max: 8 },
      },
      {
        id: 'berkeley',
        name: 'Berkeley',
        fruitType: 'blueberry',
        speciesGroup: 'northern-highbush',
        speciesGroupLabel: 'Northern highbush',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 8 },
      },
      {
        id: 'bluejay',
        name: 'Bluejay',
        fruitType: 'blueberry',
        speciesGroup: 'northern-highbush',
        speciesGroupLabel: 'Northern highbush',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 4, max: 7 },
      },
      {
        id: 'blueray',
        name: 'Blueray',
        fruitType: 'blueberry',
        speciesGroup: 'northern-highbush',
        speciesGroupLabel: 'Northern highbush',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 4, max: 7 },
      },
      {
        id: 'jersey',
        name: 'Jersey',
        fruitType: 'blueberry',
        speciesGroup: 'northern-highbush',
        speciesGroupLabel: 'Northern highbush',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid-late',
        bloomLabel: 'mid-late bloom',
        bloomOrder: 4,
        zoneRange: { min: 4, max: 7 },
      },
      {
        id: 'legacy',
        name: 'Legacy',
        fruitType: 'blueberry',
        speciesGroup: 'southern-highbush',
        speciesGroupLabel: 'Southern highbush / highbush hybrid',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 8 },
        notes: ['OSU notes that Legacy can fruit alone, but pairing it with another highbush type improves berry size and quantity.'],
      },
      {
        id: 'liberty',
        name: 'Liberty',
        fruitType: 'blueberry',
        speciesGroup: 'northern-highbush',
        speciesGroupLabel: 'Northern highbush',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid-late',
        bloomLabel: 'mid-late bloom',
        bloomOrder: 4,
        zoneRange: { min: 4, max: 7 },
      },
      {
        id: 'chandler',
        name: 'Chandler',
        fruitType: 'blueberry',
        speciesGroup: 'northern-highbush',
        speciesGroupLabel: 'Northern highbush',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid-late',
        bloomLabel: 'mid-late bloom',
        bloomOrder: 4,
        zoneRange: { min: 5, max: 8 },
      },
      {
        id: 'elliott',
        name: 'Elliott',
        fruitType: 'blueberry',
        speciesGroup: 'northern-highbush',
        speciesGroupLabel: 'Northern highbush',
        selfFertility: 'self-fertile',
        bloomGroup: 'late',
        bloomLabel: 'late bloom',
        bloomOrder: 5,
        zoneRange: { min: 5, max: 8 },
      },
      {
        id: 'toro',
        name: 'Toro',
        fruitType: 'blueberry',
        speciesGroup: 'northern-highbush',
        speciesGroupLabel: 'Northern highbush',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 4, max: 8 },
      },
      {
        id: 'aurora',
        name: 'Aurora',
        fruitType: 'blueberry',
        speciesGroup: 'northern-highbush',
        speciesGroupLabel: 'Northern highbush',
        selfFertility: 'self-fertile',
        bloomGroup: 'late',
        bloomLabel: 'late bloom',
        bloomOrder: 5,
        zoneRange: { min: 5, max: 8 },
      },
      {
        id: 'emerald',
        name: 'Emerald',
        fruitType: 'blueberry',
        speciesGroup: 'southern-highbush',
        speciesGroupLabel: 'Southern highbush',
        selfFertility: 'partially-self-fertile',
        bloomGroup: 'early',
        bloomLabel: 'early bloom',
        bloomOrder: 2,
        zoneRange: { min: 7, max: 10 },
        chillHours: 'Low chill',
      },
      {
        id: 'jewel',
        name: 'Jewel',
        fruitType: 'blueberry',
        speciesGroup: 'southern-highbush',
        speciesGroupLabel: 'Southern highbush',
        selfFertility: 'partially-self-fertile',
        bloomGroup: 'early',
        bloomLabel: 'early bloom',
        bloomOrder: 2,
        zoneRange: { min: 7, max: 10 },
        chillHours: 'Low chill',
      },
      {
        id: 'misty',
        name: 'Misty',
        fruitType: 'blueberry',
        speciesGroup: 'southern-highbush',
        speciesGroupLabel: 'Southern highbush',
        selfFertility: 'partially-self-fertile',
        bloomGroup: 'early',
        bloomLabel: 'early bloom',
        bloomOrder: 2,
        zoneRange: { min: 7, max: 10 },
        chillHours: 'Low chill',
      },
      {
        id: 'oneal',
        name: "O'Neal",
        fruitType: 'blueberry',
        speciesGroup: 'southern-highbush',
        speciesGroupLabel: 'Southern highbush',
        selfFertility: 'partially-self-fertile',
        bloomGroup: 'early',
        bloomLabel: 'early bloom',
        bloomOrder: 2,
        zoneRange: { min: 7, max: 10 },
        chillHours: 'Low chill',
      },
      {
        id: 'star',
        name: 'Star',
        fruitType: 'blueberry',
        speciesGroup: 'southern-highbush',
        speciesGroupLabel: 'Southern highbush',
        selfFertility: 'partially-self-fertile',
        bloomGroup: 'early',
        bloomLabel: 'early bloom',
        bloomOrder: 2,
        zoneRange: { min: 7, max: 10 },
        chillHours: 'Low chill',
      },
      {
        id: 'sunshine-blue',
        name: 'Sunshine Blue',
        fruitType: 'blueberry',
        speciesGroup: 'southern-highbush',
        speciesGroupLabel: 'Southern highbush',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 6, max: 10 },
        chillHours: 'Low chill',
      },
      {
        id: 'brightwell',
        name: 'Brightwell',
        fruitType: 'blueberry',
        speciesGroup: 'rabbiteye',
        speciesGroupLabel: 'Rabbiteye blueberry',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 7, max: 9 },
      },
      {
        id: 'climax',
        name: 'Climax',
        fruitType: 'blueberry',
        speciesGroup: 'rabbiteye',
        speciesGroupLabel: 'Rabbiteye blueberry',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'early',
        bloomLabel: 'early bloom',
        bloomOrder: 2,
        zoneRange: { min: 7, max: 9 },
      },
      {
        id: 'pink-lemonade',
        name: 'Pink Lemonade',
        fruitType: 'blueberry',
        speciesGroup: 'rabbiteye',
        speciesGroupLabel: 'Rabbiteye blueberry',
        selfFertility: 'partially-self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 7, max: 9 },
        notes: ['OSU notes that Pink Lemonade can fruit alone, but another rabbiteye partner improves size and quantity.'],
      },
      {
        id: 'powderblue',
        name: 'Powderblue',
        fruitType: 'blueberry',
        speciesGroup: 'rabbiteye',
        speciesGroupLabel: 'Rabbiteye blueberry',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'mid-late',
        bloomLabel: 'mid-late bloom',
        bloomOrder: 4,
        zoneRange: { min: 7, max: 9 },
      },
      {
        id: 'premier',
        name: 'Premier',
        fruitType: 'blueberry',
        speciesGroup: 'rabbiteye',
        speciesGroupLabel: 'Rabbiteye blueberry',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'early',
        bloomLabel: 'early bloom',
        bloomOrder: 2,
        zoneRange: { min: 7, max: 9 },
      },
      {
        id: 'tifblue',
        name: 'Tifblue',
        fruitType: 'blueberry',
        speciesGroup: 'rabbiteye',
        speciesGroupLabel: 'Rabbiteye blueberry',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'mid-late',
        bloomLabel: 'mid-late bloom',
        bloomOrder: 4,
        zoneRange: { min: 7, max: 9 },
      },
      {
        id: 'chippewa',
        name: 'Chippewa',
        fruitType: 'blueberry',
        speciesGroup: 'half-high',
        speciesGroupLabel: 'Half-high blueberry',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 3, max: 7 },
      },
      {
        id: 'northblue',
        name: 'Northblue',
        fruitType: 'blueberry',
        speciesGroup: 'half-high',
        speciesGroupLabel: 'Half-high blueberry',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 3, max: 7 },
      },
      {
        id: 'northcountry',
        name: 'Northcountry',
        fruitType: 'blueberry',
        speciesGroup: 'half-high',
        speciesGroupLabel: 'Half-high blueberry',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 3, max: 7 },
      },
      {
        id: 'northland',
        name: 'Northland',
        fruitType: 'blueberry',
        speciesGroup: 'half-high',
        speciesGroupLabel: 'Half-high blueberry',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 3, max: 7 },
      },
      {
        id: 'polaris',
        name: 'Polaris',
        fruitType: 'blueberry',
        speciesGroup: 'half-high',
        speciesGroupLabel: 'Half-high blueberry',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'early-mid',
        bloomLabel: 'early-mid bloom',
        bloomOrder: 2,
        zoneRange: { min: 3, max: 7 },
      },
    ],
  },
  peach: {
    id: 'peach',
    label: 'Peach',
    description: 'Most peaches can fruit with their own pollen, but another peach nearby often improves fruit set, crop consistency, and fruit size. In cool spring weather that extra overlap can be helpful.',
    partnerDistanceNote: 'If you plant two peaches, keep them close enough for bees to move easily between them. A second peach within about 50 feet is usually plenty in a home garden.',
    minimumSetup: 'One self-fertile peach is enough to get fruit in most home gardens.',
    idealSetup: 'Plant two peaches or a peach plus a nectarine with overlapping bloom to improve fruit set, fruit size, and spring backup.',
    fallbackPlan: 'If you have one self-fertile peach already, add another peach only if you want heavier crops or better odds after erratic spring weather.',
    varieties: [
      {
        id: 'belle-of-georgia',
        name: 'Belle of Georgia',
        fruitType: 'peach',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 8 },
        chillHours: 'About 800 chill hours',
      },
      {
        id: 'contender',
        name: 'Contender',
        fruitType: 'peach',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 4, max: 8 },
        chillHours: 'About 1000 chill hours',
      },
      {
        id: 'cresthaven',
        name: 'Cresthaven',
        fruitType: 'peach',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid-late',
        bloomLabel: 'mid-late bloom',
        bloomOrder: 4,
        zoneRange: { min: 5, max: 8 },
        chillHours: 'About 850 chill hours',
      },
      {
        id: 'elberta',
        name: 'Elberta',
        fruitType: 'peach',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 8 },
        chillHours: 'About 800 chill hours',
      },
      {
        id: 'flordaprince',
        name: 'Flordaprince',
        fruitType: 'peach',
        selfFertility: 'self-fertile',
        bloomGroup: 'early',
        bloomLabel: 'early bloom',
        bloomOrder: 2,
        zoneRange: { min: 8, max: 10 },
        chillHours: 'Low chill, around 150 chill hours',
      },
      {
        id: 'frost',
        name: 'Frost',
        fruitType: 'peach',
        selfFertility: 'self-fertile',
        bloomGroup: 'late',
        bloomLabel: 'late bloom',
        bloomOrder: 5,
        zoneRange: { min: 5, max: 9 },
        chillHours: 'About 700 chill hours',
      },
      {
        id: 'halehaven',
        name: 'Halehaven',
        fruitType: 'peach',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 8 },
        chillHours: 'About 850 chill hours',
        chillBand: 'high',
      },
      {
        id: 'j-h-hale',
        name: 'J.H. Hale',
        fruitType: 'peach',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 8 },
        pollenSterile: true,
        chillHours: 'About 850 chill hours',
        warnings: ['This is the major exception among peaches. It needs another peach because its own pollen is not dependable.'],
      },
      {
        id: 'july-elberta',
        name: 'July Elberta',
        fruitType: 'peach',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 8 },
        chillHours: 'About 800 chill hours',
      },
      {
        id: 'o-henry',
        name: "O'Henry",
        fruitType: 'peach',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid-late',
        bloomLabel: 'mid-late bloom',
        bloomOrder: 4,
        zoneRange: { min: 6, max: 9 },
        chillHours: 'About 750 chill hours',
      },
      {
        id: 'redhaven',
        name: 'Redhaven',
        fruitType: 'peach',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 8 },
        chillHours: 'About 800 chill hours',
      },
      {
        id: 'redskin',
        name: 'Redskin',
        fruitType: 'peach',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 8 },
        chillHours: 'About 850 chill hours',
      },
      {
        id: 'madison',
        name: 'Madison',
        fruitType: 'peach',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid-late',
        bloomLabel: 'mid-late bloom',
        bloomOrder: 4,
        zoneRange: { min: 5, max: 8 },
        chillHours: 'About 850 chill hours',
        chillBand: 'high',
      },
      {
        id: 'reliance',
        name: 'Reliance',
        fruitType: 'peach',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 4, max: 8 },
        chillHours: 'About 1000 chill hours',
        notes: ['Often chosen for colder climates. Another peach nearby can still help in shaky spring weather.'],
      },
      {
        id: 'saturn',
        name: 'Saturn',
        fruitType: 'peach',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 8 },
        chillHours: 'About 750 chill hours',
        chillBand: 'medium',
        notes: ['Flat donut peach that can fruit alone, but another mid-bloom peach nearby still improves backup and crop consistency.'],
      },
      {
        id: 'white-lady',
        name: 'White Lady',
        fruitType: 'peach',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 9 },
        chillHours: 'About 800 chill hours',
        chillBand: 'medium',
        notes: ['White-fleshed peach. Bloom timing is handled conservatively as mid bloom, so another mid-bloom peach is the safest partner choice.'],
      },
      {
        id: 'veteran',
        name: 'Veteran',
        fruitType: 'peach',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 8 },
        chillHours: 'About 800 chill hours',
        chillBand: 'medium',
      },
    ],
  },
  mango: {
    id: 'mango',
    label: 'Mango',
    description: 'Most named mangoes can fruit with their own pollen, but a second mango nearby often improves fruit set, fruit size, and crop reliability. Climate matters more than pollination in many plantings, because mangoes only perform well outdoors where frost is rare.',
    partnerDistanceNote: 'Keep mango partners close enough for bees and other blossom visitors to move easily between trees. In container or greenhouse plantings, even a second tree nearby can help when bloom weather is uneven.',
    minimumSetup: 'One named mango can fruit in a warm enough climate, protected greenhouse, or large container setup.',
    idealSetup: 'Plant two mango cultivars with overlapping bloom if your climate supports them, especially if you want steadier crops and better fruit set during erratic bloom weather.',
    fallbackPlan: 'If you garden outside frost-free conditions, solve winter protection and heat first. A second mango helps, but climate fit matters more than adding another cultivar.',
    varieties: [
      {
        id: 'tommy-atkins',
        name: 'Tommy Atkins',
        fruitType: 'mango',
        aliases: ['Tommy'],
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 10, max: 11 },
        chillHours: 'No chill requirement',
        chillBand: 'very-low',
        notes: ['Widely sold store mango. It usually fruits alone, but another mango nearby can improve crop consistency.'],
      },
      {
        id: 'kent',
        name: 'Kent',
        fruitType: 'mango',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid-late',
        bloomLabel: 'mid-late bloom',
        bloomOrder: 4,
        zoneRange: { min: 10, max: 11 },
        chillHours: 'No chill requirement',
        chillBand: 'very-low',
      },
      {
        id: 'keitt',
        name: 'Keitt',
        fruitType: 'mango',
        selfFertility: 'self-fertile',
        bloomGroup: 'late',
        bloomLabel: 'late bloom',
        bloomOrder: 5,
        zoneRange: { min: 10, max: 11 },
        chillHours: 'No chill requirement',
        chillBand: 'very-low',
        notes: ['Often blooms and ripens a little later than many common mangoes, so it benefits most from other mid-late to late mango partners.'],
      },
      {
        id: 'haden',
        name: 'Haden',
        fruitType: 'mango',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 10, max: 11 },
        chillHours: 'No chill requirement',
        chillBand: 'very-low',
      },
      {
        id: 'ataulfo-honey',
        name: 'Ataulfo / Honey',
        aliases: ['Ataulfo', 'Honey', 'Honey mango', 'Champagne'],
        fruitType: 'mango',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 10, max: 11 },
        chillHours: 'No chill requirement',
        chillBand: 'very-low',
        notes: ['Store fruit may be sold as honey or champagne mango. Flowering and fruiting are still best in warm, low-frost conditions.'],
      },
      {
        id: 'alphonso',
        name: 'Alphonso',
        fruitType: 'mango',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 10, max: 11 },
        chillHours: 'No chill requirement',
        chillBand: 'very-low',
      },
      {
        id: 'nam-doc-mai',
        name: 'Nam Doc Mai',
        aliases: ['Nam Dok Mai'],
        fruitType: 'mango',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 10, max: 11 },
        chillHours: 'No chill requirement',
        chillBand: 'very-low',
      },
      {
        id: 'carrie',
        name: 'Carrie',
        fruitType: 'mango',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 10, max: 11 },
        chillHours: 'No chill requirement',
        chillBand: 'very-low',
      },
      {
        id: 'valencia-pride',
        name: 'Valencia Pride',
        fruitType: 'mango',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid-late',
        bloomLabel: 'mid-late bloom',
        bloomOrder: 4,
        zoneRange: { min: 10, max: 11 },
        chillHours: 'No chill requirement',
        chillBand: 'very-low',
        notes: ['Large vigorous tree. In small spaces, climate and size control are usually a bigger concern than pollination.'],
      },
      {
        id: 'glenn',
        name: 'Glenn',
        fruitType: 'mango',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 10, max: 11 },
        chillHours: 'No chill requirement',
        chillBand: 'very-low',
      },
      {
        id: 'manila',
        name: 'Manila',
        fruitType: 'mango',
        selfFertility: 'self-fertile',
        bloomGroup: 'early-mid',
        bloomLabel: 'early-mid bloom',
        bloomOrder: 2,
        zoneRange: { min: 10, max: 11 },
        chillHours: 'No chill requirement',
        chillBand: 'very-low',
        notes: ['Often used as a practical backyard mango in warm dry climates and for container training where winters are protected.'],
      },
      {
        id: 'julie',
        name: 'Julie',
        fruitType: 'mango',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 10, max: 11 },
        chillHours: 'No chill requirement',
        chillBand: 'very-low',
        notes: ['Compact habit makes it attractive for protected small-space tropical plantings, but it still needs warmth.'],
      },
      {
        id: 'pickering',
        name: 'Pickering',
        fruitType: 'mango',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 9, max: 11 },
        chillHours: 'No chill requirement',
        chillBand: 'very-low',
        notes: ['Compact mango often recommended for containers or small protected patios. In zone 9 it still usually needs winter protection.'],
      },
      {
        id: 'cogshall',
        name: 'Cogshall',
        fruitType: 'mango',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 9, max: 11 },
        chillHours: 'No chill requirement',
        chillBand: 'very-low',
        notes: ['Compact mango suited to container culture in warm protected settings.'],
      },
    ],
  },
  pawpaw: {
    id: 'pawpaw',
    label: 'Pawpaw',
    description: 'Pawpaws usually need pollen from a different pawpaw cultivar. Flowers can be slow to attract pollinators, so a second cultivar nearby is important and hand pollination can boost fruit set.',
    partnerDistanceNote: 'Plant pawpaws close enough for beetles, flies, and other blossom visitors to move easily between them. In home orchards, closer is better.',
    minimumSetup: 'Plant at least two different pawpaw cultivars.',
    idealSetup: 'Plant two to three different pawpaw cultivars close together for better overlap and more dependable fruit set.',
    fallbackPlan: 'If you already planted one pawpaw, add a second named cultivar nearby rather than waiting to see if the single tree surprises you.',
    varieties: [
      {
        id: 'shenandoah',
        name: 'Shenandoah',
        fruitType: 'pawpaw',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 8 },
        notes: ['Needs a genetically different pawpaw nearby for the safest fruit set.'],
      },
      {
        id: 'susquehanna',
        name: 'Susquehanna',
        fruitType: 'pawpaw',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'mid-late',
        bloomLabel: 'mid-late bloom',
        bloomOrder: 4,
        zoneRange: { min: 5, max: 8 },
      },
      {
        id: 'allegheny',
        name: 'Allegheny',
        fruitType: 'pawpaw',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 8 },
      },
      {
        id: 'sunflower',
        name: 'Sunflower',
        fruitType: 'pawpaw',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 9 },
        warnings: ['Often marketed as unusually easy, but it is still safer to plan on a second pawpaw cultivar.'],
      },
      {
        id: 'nc-1',
        name: 'NC-1',
        fruitType: 'pawpaw',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'early-mid',
        bloomLabel: 'early-mid bloom',
        bloomOrder: 2,
        zoneRange: { min: 5, max: 8 },
      },
      {
        id: 'mango',
        name: 'Mango',
        fruitType: 'pawpaw',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 8 },
      },
      {
        id: 'pa-golden',
        name: 'PA Golden',
        fruitType: 'pawpaw',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'early-mid',
        bloomLabel: 'early-mid bloom',
        bloomOrder: 2,
        zoneRange: { min: 5, max: 8 },
        notes: ['Often treated as an earlier pawpaw bloomer, so another early to mid-bloom pawpaw is the safer match.'],
      },
    ],
  },
  persimmon: {
    id: 'persimmon',
    label: 'Persimmon',
    description: 'Many named persimmon cultivars can fruit on their own, but a second compatible persimmon often improves reliability and can increase the number of well-filled fruit. American and Asian persimmons are best matched within their own group.',
    partnerDistanceNote: 'If you plant more than one persimmon, keep them close enough for bees to work both trees during bloom. In home plantings, about 50 feet or less is a good target.',
    minimumSetup: 'One self-fruitful persimmon can work, but two compatible persimmons usually give you steadier crops.',
    idealSetup: 'Plant two compatible persimmons from the same group, such as two Asian or two American cultivars, for better fruit set and more backup.',
    fallbackPlan: 'If you already planted one self-fruitful persimmon, add a second compatible cultivar only if you want stronger yield and a better buffer against weak bloom years.',
    varieties: [
      {
        id: 'meader',
        name: 'Meader',
        fruitType: 'persimmon',
        speciesGroup: 'american',
        speciesGroupLabel: 'American persimmon',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 9 },
      },
      {
        id: 'prok',
        name: 'Prok',
        fruitType: 'persimmon',
        speciesGroup: 'american',
        speciesGroupLabel: 'American persimmon',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 9 },
      },
      {
        id: 'prairie-star',
        name: 'Prairie Star',
        fruitType: 'persimmon',
        speciesGroup: 'american',
        speciesGroupLabel: 'American persimmon',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 9 },
        notes: ['Practical conservative default: treat it like another mid-bloom American persimmon and pair it within the American group.'],
      },
      {
        id: 'szukis',
        name: 'Szukis',
        fruitType: 'persimmon',
        speciesGroup: 'american',
        speciesGroupLabel: 'American persimmon',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 9 },
        notes: ['Practical conservative default: keep it with other American persimmons that bloom around mid-season.'],
      },
      {
        id: 'yates',
        name: 'Yates',
        fruitType: 'persimmon',
        speciesGroup: 'american',
        speciesGroupLabel: 'American persimmon',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 9 },
      },
      {
        id: 'fuyu',
        name: 'Fuyu',
        fruitType: 'persimmon',
        speciesGroup: 'asian',
        speciesGroupLabel: 'Asian persimmon',
        selfFertility: 'self-fertile',
        bloomGroup: 'late',
        bloomLabel: 'late bloom',
        bloomOrder: 4,
        zoneRange: { min: 7, max: 10 },
        chillHours: 'About 200 chill hours',
        chillBand: 'low',
      },
      {
        id: 'hachiya',
        name: 'Hachiya',
        fruitType: 'persimmon',
        speciesGroup: 'asian',
        speciesGroupLabel: 'Asian persimmon',
        selfFertility: 'self-fertile',
        bloomGroup: 'late',
        bloomLabel: 'late bloom',
        bloomOrder: 4,
        zoneRange: { min: 7, max: 10 },
        chillHours: 'About 200 chill hours',
        chillBand: 'low',
      },
      {
        id: 'saijo',
        name: 'Saijo',
        fruitType: 'persimmon',
        speciesGroup: 'asian',
        speciesGroupLabel: 'Asian persimmon',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid-late',
        bloomLabel: 'mid-late bloom',
        bloomOrder: 4,
        zoneRange: { min: 7, max: 9 },
        chillHours: 'About 200 chill hours',
        chillBand: 'low',
      },
      {
        id: 'izu',
        name: 'Izu',
        fruitType: 'persimmon',
        speciesGroup: 'asian',
        speciesGroupLabel: 'Asian persimmon',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid-late',
        bloomLabel: 'mid-late bloom',
        bloomOrder: 4,
        zoneRange: { min: 7, max: 10 },
        chillHours: 'About 200 chill hours',
        chillBand: 'low',
      },
    ],
  },
  apricot: {
    id: 'apricot',
    label: 'Apricot',
    description: 'Many apricots can fruit alone, but another apricot often improves the crop. A few named varieties really do need a partner, so bloom overlap still matters.',
    partnerDistanceNote: 'Keep apricot partners close enough for bees to move easily between them. Apricots flower early, so reliable overlap matters in cool springs.',
    minimumSetup: 'One self-fruitful apricot can work, but two apricots are safer if you want steadier crops.',
    idealSetup: 'Plant two apricot cultivars with overlapping bloom so you get better fruit set and a backup if one tree blooms weakly.',
    fallbackPlan: 'If you already have a partner-dependent apricot, add a second apricot cultivar instead of waiting for self-pollination that may never come.',
    varieties: [
      {
        id: 'blenheim',
        name: 'Blenheim',
        fruitType: 'apricot',
        selfFertility: 'self-fertile',
        bloomGroup: 'early-mid',
        bloomLabel: 'early-mid bloom',
        bloomOrder: 2,
        zoneRange: { min: 6, max: 9 },
      },
      {
        id: 'goldcot',
        name: 'Goldcot',
        fruitType: 'apricot',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 4, max: 8 },
      },
      {
        id: 'harcot',
        name: 'Harcot',
        fruitType: 'apricot',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 8 },
      },
      {
        id: 'moorpark',
        name: 'Moorpark',
        fruitType: 'apricot',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 8 },
      },
      {
        id: 'chinese-mormon',
        name: 'Chinese / Mormon',
        aliases: ['Chinese', 'Mormon', 'Chinese/Mormon varieties'],
        fruitType: 'apricot',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 8 },
        notes: ['This plant-page label covers closely related nursery naming. A second apricot is still the safer choice when you want better fruit set.'],
      },
      {
        id: 'perfection',
        name: 'Perfection',
        fruitType: 'apricot',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 8 },
      },
      {
        id: 'tilton',
        name: 'Tilton',
        fruitType: 'apricot',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 8 },
      },
      {
        id: 'hargrand',
        name: 'Hargrand',
        fruitType: 'apricot',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 8 },
      },
      {
        id: 'harlayne',
        name: 'Harlayne',
        fruitType: 'apricot',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid-late',
        bloomLabel: 'mid-late bloom',
        bloomOrder: 4,
        zoneRange: { min: 5, max: 8 },
        notes: ['Often treated as a slightly later-blooming apricot, which can help where spring frost is a recurring problem.'],
      },
      {
        id: 'rival',
        name: 'Rival',
        fruitType: 'apricot',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 8 },
      },
    ],
  },
  nectarine: {
    id: 'nectarine',
    label: 'Nectarine',
    description: 'Most nectarines can fruit with their own pollen, but another compatible nectarine nearby often improves set and helps smooth out erratic spring weather.',
    partnerDistanceNote: 'If you plant two nectarines, keep them close enough for bees to move easily between them. A second tree within about 50 feet is usually enough.',
    minimumSetup: 'One self-fruitful nectarine is usually enough to get fruit.',
    idealSetup: 'Plant two nectarines with overlapping bloom if you want better fruit set, larger crops, and extra spring insurance.',
    fallbackPlan: 'If you already have one nectarine, add a second only if you want to improve crop reliability rather than simply get any fruit at all.',
    varieties: [
      {
        id: 'fantasia',
        name: 'Fantasia',
        fruitType: 'nectarine',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 9 },
        chillHours: 'About 500 chill hours',
        chillBand: 'medium',
      },
      {
        id: 'mericrest',
        name: 'Mericrest',
        fruitType: 'nectarine',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 8 },
        chillHours: 'About 800 chill hours',
        chillBand: 'high',
        notes: ['Handled conservatively as a mid-bloom nectarine. Another peach or nectarine nearby can still improve crop consistency even though it is self-fruitful.'],
      },
      {
        id: 'flavortop',
        name: 'Flavortop',
        fruitType: 'nectarine',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 9 },
        chillHours: 'About 650 chill hours',
        chillBand: 'medium',
      },
      {
        id: 'harko',
        name: 'Harko',
        fruitType: 'nectarine',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 8 },
        chillHours: 'About 800 chill hours',
        chillBand: 'high',
      },
      {
        id: 'redgold',
        name: 'Redgold',
        fruitType: 'nectarine',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 8 },
        chillHours: 'About 850 chill hours',
        chillBand: 'high',
      },
      {
        id: 'sunfre',
        name: 'Sunfre',
        fruitType: 'nectarine',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 9 },
        chillHours: 'About 525 chill hours',
        chillBand: 'medium',
      },
      {
        id: 'arctic-glo',
        name: 'Arctic Glo',
        fruitType: 'nectarine',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid-late',
        bloomLabel: 'mid-late bloom',
        bloomOrder: 4,
        zoneRange: { min: 6, max: 9 },
        chillHours: 'About 800 chill hours',
        chillBand: 'high',
      },
      {
        id: 'necta-zee',
        name: 'Necta Zee',
        fruitType: 'nectarine',
        selfFertility: 'self-fertile',
        bloomGroup: 'early-mid',
        bloomLabel: 'early-mid bloom',
        bloomOrder: 2,
        zoneRange: { min: 6, max: 9 },
        chillHours: 'About 200 chill hours',
        chillBand: 'low',
      },
    ],
  },
  mulberry: {
    id: 'mulberry',
    label: 'Mulberry',
    description: 'Many named mulberry cultivars can fruit on their own, but an extra mulberry nearby can still improve fruiting and gives you a longer harvest window. Seedling mulberries are less predictable because some are male and never fruit.',
    partnerDistanceNote: 'Mulberries are often wind-pollinated, so close spacing helps but they do not need to be packed as tightly as pawpaws or blueberries.',
    minimumSetup: 'One named self-fruitful mulberry is usually enough for home harvests.',
    idealSetup: 'Plant two named fruiting mulberries if you want more fruit, a longer harvest, and a backup if one tree fruits lightly.',
    fallbackPlan: 'If you already have an unknown seedling mulberry, wait to confirm it fruits before counting on it as your only tree.',
    varieties: [
      {
        id: 'illinois-everbearing',
        name: 'Illinois Everbearing',
        fruitType: 'mulberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 4, max: 8 },
      },
      {
        id: 'dwarf-everbearing',
        name: 'Dwarf Everbearing',
        fruitType: 'mulberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 10 },
      },
      {
        id: 'pakistan',
        name: 'Oscar / Pakistan Mulberry',
        aliases: ['Oscar', 'Pakistan', 'Pakistan Mulberry'],
        fruitType: 'mulberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid-late',
        bloomLabel: 'mid-late bloom',
        bloomOrder: 4,
        zoneRange: { min: 8, max: 10 },
      },
      {
        id: 'hicks-everbearing',
        name: 'Hicks Everbearing',
        fruitType: 'mulberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 6, max: 9 },
      },
      {
        id: 'kokuso',
        name: 'Kokuso',
        fruitType: 'mulberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 9 },
      },
      {
        id: 'wellington',
        name: 'Wellington',
        fruitType: 'mulberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 8 },
      },
      {
        id: 'shangri-la',
        name: 'Shangri-La',
        fruitType: 'mulberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid-late',
        bloomLabel: 'mid-late bloom',
        bloomOrder: 4,
        zoneRange: { min: 7, max: 10 },
      },
      {
        id: 'persian',
        name: 'Persian',
        fruitType: 'mulberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 7, max: 9 },
        notes: ['Sometimes sold as black mulberry. In cooler climates the plant itself is usually the limiting factor, not pollination.'],
      },
    ],
  },
  elderberry: {
    id: 'elderberry',
    label: 'Elderberry',
    description: 'Elderberries often fruit with one cultivar, but yields are usually heavier and more reliable when two or more named cultivars bloom together.',
    partnerDistanceNote: 'Keep elderberries in the same row or nearby rows so pollinators can move easily among the flower clusters.',
    minimumSetup: 'One elderberry can fruit, but planting two cultivars is the better baseline.',
    idealSetup: 'Plant two to three elderberry cultivars with overlapping bloom so crop size and berry fill stay strong.',
    fallbackPlan: 'If you already have one elderberry, add a second named cultivar before deciding the original plant is unproductive.',
    varieties: [
      {
        id: 'adams',
        name: 'Adams',
        fruitType: 'elderberry',
        speciesGroup: 'american',
        speciesGroupLabel: 'American elderberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 3, max: 9 },
      },
      {
        id: 'johns',
        name: 'Johns',
        fruitType: 'elderberry',
        speciesGroup: 'american',
        speciesGroupLabel: 'American elderberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 3, max: 9 },
      },
      {
        id: 'nova',
        name: 'Nova',
        fruitType: 'elderberry',
        speciesGroup: 'american',
        speciesGroupLabel: 'American elderberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 4, max: 8 },
      },
      {
        id: 'ranch',
        name: 'Ranch',
        fruitType: 'elderberry',
        speciesGroup: 'american',
        speciesGroupLabel: 'American elderberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 3, max: 8 },
        notes: ['Often grown as a hardy, practical elderberry. Another elderberry still improves yield and berry fill.'],
      },
      {
        id: 'york',
        name: 'York',
        fruitType: 'elderberry',
        speciesGroup: 'american',
        speciesGroupLabel: 'American elderberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid-late',
        bloomLabel: 'mid-late bloom',
        bloomOrder: 4,
        zoneRange: { min: 4, max: 8 },
      },
      {
        id: 'bob-gordon',
        name: 'Bob Gordon',
        fruitType: 'elderberry',
        speciesGroup: 'american',
        speciesGroupLabel: 'American elderberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid-late',
        bloomLabel: 'mid-late bloom',
        bloomOrder: 4,
        zoneRange: { min: 4, max: 8 },
      },
      {
        id: 'marge',
        name: 'Marge',
        fruitType: 'elderberry',
        speciesGroup: 'european-hybrid',
        speciesGroupLabel: 'European or hybrid elderberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 8 },
        notes: ['Often grouped with European or hybrid elderberries in nursery listings. Another elderberry still improves yield.'],
      },
      {
        id: 'scotia',
        name: 'Scotia',
        fruitType: 'elderberry',
        speciesGroup: 'american',
        speciesGroupLabel: 'American elderberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 4, max: 8 },
      },
      {
        id: 'haschberg',
        name: 'Haschberg',
        fruitType: 'elderberry',
        speciesGroup: 'european-hybrid',
        speciesGroupLabel: 'European or hybrid elderberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid-late',
        bloomLabel: 'mid-late bloom',
        bloomOrder: 4,
        zoneRange: { min: 5, max: 8 },
      },
      {
        id: 'wyldewood',
        name: 'Wyldewood',
        fruitType: 'elderberry',
        speciesGroup: 'american',
        speciesGroupLabel: 'American elderberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid-late',
        bloomLabel: 'mid-late bloom',
        bloomOrder: 4,
        zoneRange: { min: 4, max: 8 },
      },
    ],
  },
  fig: {
    id: 'fig',
    label: 'Fig',
    description: 'Common figs do not need a second tree for pollination in most home gardens. A second fig is still useful if you want more total fruit, a longer harvest, or backup after winter damage.',
    partnerDistanceNote: 'Figs do not need a nearby partner for normal home-garden fruiting, but grouping two varieties can still be handy for harvest timing and insurance.',
    minimumSetup: 'One common fig is enough for fruit in most home plantings.',
    idealSetup: 'Plant two common figs only if you want a longer season, more total harvest, or a backup after cold damage.',
    fallbackPlan: 'If a fig is not fruiting well, fix winter injury, sunlight, and pruning first before assuming it needs a pollination partner.',
    varieties: [
      {
        id: 'brown-turkey',
        name: 'Brown Turkey',
        fruitType: 'fig',
        selfFertility: 'self-fertile',
        bloomGroup: 'summer',
        bloomLabel: 'summer fruiting season',
        bloomOrder: 3,
        zoneRange: { min: 7, max: 10 },
        chillHours: 'Less than 300 chill hours',
        chillBand: 'very-low',
      },
      {
        id: 'chicago-hardy',
        name: 'Chicago Hardy',
        fruitType: 'fig',
        selfFertility: 'self-fertile',
        bloomGroup: 'summer',
        bloomLabel: 'summer fruiting season',
        bloomOrder: 3,
        zoneRange: { min: 6, max: 10 },
        chillHours: 'Less than 300 chill hours',
        chillBand: 'very-low',
      },
      {
        id: 'celeste',
        name: 'Celeste',
        fruitType: 'fig',
        selfFertility: 'self-fertile',
        bloomGroup: 'early-summer',
        bloomLabel: 'early summer fruiting season',
        bloomOrder: 2,
        zoneRange: { min: 7, max: 10 },
        chillHours: 'Less than 300 chill hours',
        chillBand: 'very-low',
      },
      {
        id: 'black-mission',
        name: 'Black Mission',
        fruitType: 'fig',
        selfFertility: 'self-fertile',
        bloomGroup: 'summer',
        bloomLabel: 'summer fruiting season',
        bloomOrder: 3,
        zoneRange: { min: 7, max: 10 },
        chillHours: 'Less than 300 chill hours',
        chillBand: 'very-low',
      },
      {
        id: 'desert-king',
        name: 'Desert King',
        fruitType: 'fig',
        selfFertility: 'self-fertile',
        bloomGroup: 'early-summer',
        bloomLabel: 'early summer fruiting season',
        bloomOrder: 2,
        zoneRange: { min: 7, max: 10 },
        chillHours: 'Less than 300 chill hours',
        chillBand: 'very-low',
        notes: ['Often valued where the early crop is more dependable than the main crop.'],
      },
      {
        id: 'kadota',
        name: 'Kadota',
        fruitType: 'fig',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid-summer',
        bloomLabel: 'mid-summer fruiting season',
        bloomOrder: 4,
        zoneRange: { min: 7, max: 10 },
        chillHours: 'Less than 300 chill hours',
        chillBand: 'very-low',
      },
      {
        id: 'lsu-purple',
        name: 'LSU Purple',
        fruitType: 'fig',
        selfFertility: 'self-fertile',
        bloomGroup: 'summer',
        bloomLabel: 'summer fruiting season',
        bloomOrder: 3,
        zoneRange: { min: 7, max: 10 },
        chillHours: 'Less than 300 chill hours',
        chillBand: 'very-low',
      },
      {
        id: 'violette-de-bordeaux',
        name: 'Violette de Bordeaux',
        fruitType: 'fig',
        selfFertility: 'self-fertile',
        bloomGroup: 'summer',
        bloomLabel: 'summer fruiting season',
        bloomOrder: 3,
        zoneRange: { min: 7, max: 10 },
        chillHours: 'Less than 300 chill hours',
        chillBand: 'very-low',
      },
    ],
  },
  avocado: {
    id: 'avocado',
    label: 'Avocado',
    description: 'Avocados can set some fruit alone, but many home plantings crop better when Type A and Type B varieties bloom near each other. Weather, tree age, and bee activity still matter a lot.',
    partnerDistanceNote: 'Plant Type A and Type B avocado varieties close enough for bees to visit both trees during the same bloom period. In small gardens, nearby spacing is usually more useful than distant orchard spacing.',
    minimumSetup: 'One grafted avocado can sometimes fruit alone, especially in mild climates with strong bloom overlap.',
    idealSetup: 'Pair one Type A and one Type B variety with similar bloom timing for steadier fruit set and better backup in cool or uneven bloom weather.',
    fallbackPlan: 'If you only have room for one tree, choose a reliable self-fruiting variety and focus on frost protection, drainage, and pollinator activity before assuming another tree is required.',
    varieties: [
      {
        id: 'hass',
        name: 'Hass',
        fruitType: 'avocado',
        speciesGroup: 'type-a',
        speciesGroupLabel: 'Type A flowering',
        selfFertility: 'partially-self-fertile',
        bloomGroup: 'type-a',
        bloomLabel: 'Type A bloom',
        bloomOrder: 3,
        zoneRange: { min: 9, max: 11 },
        compatibleVarietyIds: ['fuerte', 'bacon', 'zutano'],
      },
      {
        id: 'reed',
        name: 'Reed',
        fruitType: 'avocado',
        speciesGroup: 'type-a',
        speciesGroupLabel: 'Type A flowering',
        selfFertility: 'partially-self-fertile',
        bloomGroup: 'type-a',
        bloomLabel: 'Type A bloom',
        bloomOrder: 3,
        zoneRange: { min: 9, max: 11 },
        compatibleVarietyIds: ['fuerte', 'bacon', 'zutano'],
      },
      {
        id: 'pinkerton',
        name: 'Pinkerton',
        fruitType: 'avocado',
        speciesGroup: 'type-a',
        speciesGroupLabel: 'Type A flowering',
        selfFertility: 'partially-self-fertile',
        bloomGroup: 'type-a',
        bloomLabel: 'Type A bloom',
        bloomOrder: 3,
        zoneRange: { min: 9, max: 11 },
        compatibleVarietyIds: ['fuerte', 'bacon', 'zutano'],
      },
      {
        id: 'fuerte',
        name: 'Fuerte',
        fruitType: 'avocado',
        speciesGroup: 'type-b',
        speciesGroupLabel: 'Type B flowering',
        selfFertility: 'partially-self-fertile',
        bloomGroup: 'type-b',
        bloomLabel: 'Type B bloom',
        bloomOrder: 3,
        zoneRange: { min: 9, max: 11 },
        compatibleVarietyIds: ['hass', 'reed', 'pinkerton'],
      },
      {
        id: 'bacon',
        name: 'Bacon',
        fruitType: 'avocado',
        speciesGroup: 'type-b',
        speciesGroupLabel: 'Type B flowering',
        selfFertility: 'partially-self-fertile',
        bloomGroup: 'type-b',
        bloomLabel: 'Type B bloom',
        bloomOrder: 3,
        zoneRange: { min: 8, max: 10 },
        compatibleVarietyIds: ['hass', 'reed', 'pinkerton'],
        notes: ['Often used as a cooler-climate Type B partner in California-style plantings.'],
      },
      {
        id: 'zutano',
        name: 'Zutano',
        fruitType: 'avocado',
        speciesGroup: 'type-b',
        speciesGroupLabel: 'Type B flowering',
        selfFertility: 'partially-self-fertile',
        bloomGroup: 'type-b',
        bloomLabel: 'Type B bloom',
        bloomOrder: 3,
        zoneRange: { min: 8, max: 10 },
        compatibleVarietyIds: ['hass', 'reed', 'pinkerton'],
      },
      {
        id: 'mexicola',
        name: 'Mexicola',
        fruitType: 'avocado',
        speciesGroup: 'type-a',
        speciesGroupLabel: 'Type A flowering',
        selfFertility: 'partially-self-fertile',
        bloomGroup: 'type-a',
        bloomLabel: 'Type A bloom',
        bloomOrder: 3,
        zoneRange: { min: 8, max: 10 },
        compatibleVarietyIds: ['bacon', 'zutano', 'fuerte'],
        notes: ['More cold-tolerant than many avocados, but fruit and young trees still need frost protection.'],
      },
    ],
  },
  'date-palm': {
    id: 'date-palm',
    label: 'Date Palm',
    description: 'Date palms are dioecious: female palms produce dates, but they need pollen from a male palm or hand pollination. Seed-grown palms may be male or female, so named female offshoots are the practical starting point.',
    partnerDistanceNote: 'A male date palm can pollinate many female palms if pollen is collected and applied by hand. In home plantings, plan for one male source or reliable pollen access.',
    minimumSetup: 'At least one female date palm plus a male pollen source is needed for fruit.',
    idealSetup: 'Plant named female offshoots and keep a compatible male palm or stored pollen available for hand pollination during bloom.',
    fallbackPlan: 'If you already have female palms but no fruit set, confirm pollen availability and hand-pollination timing before replacing the trees.',
    varieties: [
      {
        id: 'medjool',
        name: 'Medjool',
        fruitType: 'date-palm',
        pollinationRole: 'fruiting',
        speciesGroup: 'female',
        speciesGroupLabel: 'Female fruiting palm',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'spring',
        bloomLabel: 'spring bloom',
        bloomOrder: 3,
        zoneRange: { min: 9, max: 11 },
      },
      {
        id: 'deglet-noor',
        name: 'Deglet Noor',
        fruitType: 'date-palm',
        pollinationRole: 'fruiting',
        speciesGroup: 'female',
        speciesGroupLabel: 'Female fruiting palm',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'spring',
        bloomLabel: 'spring bloom',
        bloomOrder: 3,
        zoneRange: { min: 9, max: 11 },
      },
      {
        id: 'barhi',
        name: 'Barhi',
        fruitType: 'date-palm',
        pollinationRole: 'fruiting',
        speciesGroup: 'female',
        speciesGroupLabel: 'Female fruiting palm',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'spring',
        bloomLabel: 'spring bloom',
        bloomOrder: 3,
        zoneRange: { min: 9, max: 11 },
      },
      {
        id: 'zahidi',
        name: 'Zahidi',
        fruitType: 'date-palm',
        pollinationRole: 'fruiting',
        speciesGroup: 'female',
        speciesGroupLabel: 'Female fruiting palm',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'spring',
        bloomLabel: 'spring bloom',
        bloomOrder: 3,
        zoneRange: { min: 9, max: 11 },
      },
      {
        id: 'halawy',
        name: 'Halawy',
        fruitType: 'date-palm',
        pollinationRole: 'fruiting',
        speciesGroup: 'female',
        speciesGroupLabel: 'Female fruiting palm',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'spring',
        bloomLabel: 'spring bloom',
        bloomOrder: 3,
        zoneRange: { min: 9, max: 11 },
      },
      {
        id: 'date-male-pollenizer',
        name: 'Male Date Palm',
        fruitType: 'date-palm',
        pollinationRole: 'male-pollinizer',
        speciesGroup: 'male',
        speciesGroupLabel: 'Male pollen source',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'spring',
        bloomLabel: 'spring pollen shed',
        bloomOrder: 3,
        zoneRange: { min: 9, max: 11 },
        notes: ['Male palms supply pollen but do not produce edible dates. One male can provide pollen for many female palms.'],
      },
    ],
  },
  cherimoya: {
    id: 'cherimoya',
    label: 'Cherimoya',
    description: 'Cherimoya flowers pass through female and male stages, so natural fruit set can be uneven. Hand pollination often matters more than planting a second tree.',
    partnerDistanceNote: 'A second cherimoya can add genetic variety, but hand-pollinating flowers at the right stage is usually the key home-garden action.',
    minimumSetup: 'One grafted cherimoya can fruit, especially with hand pollination.',
    idealSetup: 'Grow one or two named varieties and plan to hand-pollinate flowers during bloom for reliable fruit set.',
    fallbackPlan: 'If flowers drop without fruit, learn the female and male flower stages and hand-pollinate before assuming a partner variety is missing.',
    varieties: [
      {
        id: 'booth',
        name: 'Booth',
        fruitType: 'cherimoya',
        selfFertility: 'partially-self-fertile',
        bloomGroup: 'spring',
        bloomLabel: 'spring bloom',
        bloomOrder: 3,
        zoneRange: { min: 9, max: 11 },
        notes: ['Hand pollination usually improves set and fruit shape.'],
      },
      {
        id: 'el-bumpo',
        name: 'El Bumpo',
        fruitType: 'cherimoya',
        selfFertility: 'partially-self-fertile',
        bloomGroup: 'spring',
        bloomLabel: 'spring bloom',
        bloomOrder: 3,
        zoneRange: { min: 9, max: 11 },
      },
      {
        id: 'pierce',
        name: 'Pierce',
        fruitType: 'cherimoya',
        selfFertility: 'partially-self-fertile',
        bloomGroup: 'spring',
        bloomLabel: 'spring bloom',
        bloomOrder: 3,
        zoneRange: { min: 9, max: 11 },
      },
      {
        id: 'honeyhart',
        name: 'Honeyhart',
        fruitType: 'cherimoya',
        selfFertility: 'partially-self-fertile',
        bloomGroup: 'spring',
        bloomLabel: 'spring bloom',
        bloomOrder: 3,
        zoneRange: { min: 9, max: 11 },
      },
      {
        id: 'fino-de-jete',
        name: 'Fino de Jete',
        fruitType: 'cherimoya',
        selfFertility: 'partially-self-fertile',
        bloomGroup: 'spring',
        bloomLabel: 'spring bloom',
        bloomOrder: 3,
        zoneRange: { min: 9, max: 11 },
      },
      {
        id: 'nata',
        name: 'Nata',
        fruitType: 'cherimoya',
        selfFertility: 'partially-self-fertile',
        bloomGroup: 'spring',
        bloomLabel: 'spring bloom',
        bloomOrder: 3,
        zoneRange: { min: 9, max: 11 },
      },
    ],
  },
  grape: {
    id: 'grape',
    label: 'Grape',
    description: 'Most home-garden grapes are self-fertile and do not need a second vine to make fruit. A second grape with overlapping bloom can still improve yield in some plantings and gives you a useful backup if weather is uneven.',
    partnerDistanceNote: 'If you add a second grape, keep it on the same arbor, trellis row, or nearby support so pollinators can move easily between blooms.',
    minimumSetup: 'One self-fertile grape vine is usually enough for fruit.',
    idealSetup: 'Grow two grape varieties only if you want a broader harvest window, a backup vine, or a chance at slightly better fruit set in mixed plantings.',
    fallbackPlan: 'If a grape vine is cropping lightly, fix pruning, sunlight, and winter cane damage before assuming it needs a pollination partner.',
    varieties: [
      {
        id: 'concord',
        name: 'Concord',
        fruitType: 'grape',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        speciesGroup: 'labrusca',
        speciesGroupLabel: 'Blue seeded grape',
        zoneRange: { min: 4, max: 9 },
        notes: ['Classic juice and jelly grape with reliable home-garden crops.'],
      },
      {
        id: 'niagara',
        name: 'Niagara',
        fruitType: 'grape',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        speciesGroup: 'labrusca',
        speciesGroupLabel: 'White seeded grape',
        zoneRange: { min: 5, max: 9 },
      },
      {
        id: 'vanessa',
        name: 'Vanessa',
        fruitType: 'grape',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        speciesGroup: 'seedless-table',
        speciesGroupLabel: 'Red seedless grape',
        zoneRange: { min: 5, max: 8 },
      },
      {
        id: 'himrod',
        name: 'Himrod',
        fruitType: 'grape',
        selfFertility: 'self-fertile',
        bloomGroup: 'early-mid',
        bloomLabel: 'early-mid bloom',
        bloomOrder: 2,
        speciesGroup: 'seedless-table',
        speciesGroupLabel: 'White seedless grape',
        zoneRange: { min: 5, max: 8 },
      },
      {
        id: 'reliance-grape',
        name: 'Reliance',
        fruitType: 'grape',
        selfFertility: 'self-fertile',
        bloomGroup: 'early-mid',
        bloomLabel: 'early-mid bloom',
        bloomOrder: 2,
        speciesGroup: 'seedless-table',
        speciesGroupLabel: 'Pink seedless grape',
        zoneRange: { min: 4, max: 8 },
        notes: ['Cold-climate favorite with dependable home-garden performance.'],
      },
      {
        id: 'canadice',
        name: 'Canadice',
        fruitType: 'grape',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        speciesGroup: 'seedless-table',
        speciesGroupLabel: 'Red seedless grape',
        zoneRange: { min: 5, max: 8 },
      },
      {
        id: 'mars',
        name: 'Mars',
        fruitType: 'grape',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        speciesGroup: 'seedless-table',
        speciesGroupLabel: 'Blue seedless grape',
        zoneRange: { min: 5, max: 8 },
      },
      {
        id: 'marquis',
        name: 'Marquis',
        fruitType: 'grape',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid-late',
        bloomLabel: 'mid-late bloom',
        bloomOrder: 4,
        speciesGroup: 'seedless-table',
        speciesGroupLabel: 'White seedless grape',
        zoneRange: { min: 5, max: 8 },
      },
      {
        id: 'edelweiss',
        name: 'Edelweiss',
        fruitType: 'grape',
        selfFertility: 'self-fertile',
        bloomGroup: 'early-mid',
        bloomLabel: 'early-mid bloom',
        bloomOrder: 2,
        speciesGroup: 'cold-climate',
        speciesGroupLabel: 'White table and juice grape',
        zoneRange: { min: 4, max: 8 },
      },
      {
        id: 'catawba',
        name: 'Catawba',
        fruitType: 'grape',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid-late',
        bloomLabel: 'mid-late bloom',
        bloomOrder: 4,
        speciesGroup: 'labrusca',
        speciesGroupLabel: 'Pink seeded grape',
        zoneRange: { min: 5, max: 8 },
      },
      {
        id: 'fredonia',
        name: 'Fredonia',
        fruitType: 'grape',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        speciesGroup: 'labrusca',
        speciesGroupLabel: 'Blue seeded grape',
        zoneRange: { min: 5, max: 8 },
      },
      {
        id: 'steuben',
        name: 'Steuben',
        fruitType: 'grape',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid-late',
        bloomLabel: 'mid-late bloom',
        bloomOrder: 4,
        speciesGroup: 'american-hybrid',
        speciesGroupLabel: 'Blue seeded grape',
        zoneRange: { min: 5, max: 8 },
      },
      {
        id: 'jupiter',
        name: 'Jupiter',
        fruitType: 'grape',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        speciesGroup: 'seedless-table',
        speciesGroupLabel: 'Blue seedless grape',
        zoneRange: { min: 5, max: 9 },
      },
      {
        id: 'somerset-seedless',
        name: 'Somerset Seedless',
        fruitType: 'grape',
        selfFertility: 'self-fertile',
        bloomGroup: 'early-mid',
        bloomLabel: 'early-mid bloom',
        bloomOrder: 2,
        speciesGroup: 'seedless-table',
        speciesGroupLabel: 'Red seedless grape',
        zoneRange: { min: 4, max: 8 },
      },
    ],
  },
  pomegranate: {
    id: 'pomegranate',
    label: 'Pomegranate',
    description: 'Pomegranates are mostly self-fertile, but a second plant nearby can improve fruit set and crop consistency, especially where bloom weather is uneven or pollinator activity is light.',
    partnerDistanceNote: 'If you plant more than one pomegranate, keep them close enough for bees to work both during the same bloom period.',
    minimumSetup: 'One pomegranate is usually enough for fruit.',
    idealSetup: 'Plant two pomegranates if you want a stronger backup and the best chance at fuller fruit set.',
    fallbackPlan: 'If one plant is flowering but fruit set is light, improve heat, sunlight, and pollinator activity first, then consider adding a second plant.',
    varieties: [
      {
        id: 'general-pomegranate',
        name: 'General pomegranate',
        fruitType: 'pomegranate',
        selfFertility: 'self-fertile',
        bloomGroup: 'late',
        bloomLabel: 'late bloom',
        bloomOrder: 4,
        zoneRange: { min: 7, max: 10 },
        notes: ['Most garden pomegranates can fruit alone, but a nearby second plant can still improve the crop.'],
      },
      {
        id: 'second-nearby-pomegranate',
        name: 'Second nearby pomegranate',
        fruitType: 'pomegranate',
        selfFertility: 'self-fertile',
        bloomGroup: 'late',
        bloomLabel: 'late bloom',
        bloomOrder: 4,
        zoneRange: { min: 7, max: 10 },
        notes: ['Use this when you are comparing whether a second pomegranate is worth the space in a warm-climate planting.'],
      },
    ],
  },
  'passion-fruit': {
    id: 'passion-fruit',
    label: 'Passion Fruit / Maypop',
    description: 'Some passion fruit vines are self-fertile, while others fruit better with another compatible vine nearby. Maypop also benefits from cross-pollination when the second vine is a separate compatible plant rather than just another runner from the same patch.',
    partnerDistanceNote: 'Keep compatible vines on the same trellis or nearby supports so pollinators can move between fresh flowers the same day.',
    minimumSetup: 'One self-fertile passion fruit can fruit alone, but two compatible vines are the safer plan for unknown vines and for maypop.',
    idealSetup: 'Grow two compatible vines with overlapping bloom. For maypop, use a separate plant, seedling, or source rather than relying on one spreading patch.',
    fallbackPlan: 'If flowers open well but fruit set stays poor, add another compatible vine or hand-pollinate before assuming the site is the problem.',
    varieties: [
      {
        id: 'general-passion-fruit-vine',
        name: 'General passion fruit vine',
        aliases: ['Passionfruit', 'Passion fruit'],
        fruitType: 'passion-fruit',
        speciesGroup: 'edulis',
        speciesGroupLabel: 'Passion fruit - fruiting vine',
        selfFertility: 'partially-self-fertile',
        bloomGroup: 'mid-late',
        bloomLabel: 'mid-late bloom',
        bloomOrder: 4,
        zoneRange: { min: 9, max: 11 },
        notes: ['Use this when you know the vine is passion fruit but do not know whether it is a named self-fertile selection.'],
      },
      {
        id: 'self-fertile-passion-fruit',
        name: 'Self-fertile passion fruit selection',
        fruitType: 'passion-fruit',
        speciesGroup: 'edulis',
        speciesGroupLabel: 'Passion fruit - self-fertile vine',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid-late',
        bloomLabel: 'mid-late bloom',
        bloomOrder: 4,
        zoneRange: { min: 9, max: 11 },
        notes: ['Can fruit alone, but a second compatible vine nearby often improves set and gives you backup during uneven bloom weather.'],
      },
      {
        id: 'maypop',
        name: 'Maypop',
        aliases: ['Purple Passionflower', 'Passiflora incarnata'],
        fruitType: 'passion-fruit',
        speciesGroup: 'incarnata',
        speciesGroupLabel: 'Maypop - fruiting vine',
        selfFertility: 'partially-self-fertile',
        bloomGroup: 'mid-late',
        bloomLabel: 'mid-late bloom',
        bloomOrder: 4,
        zoneRange: { min: 5, max: 9 },
        notes: ['Maypop often fruits better when pollen comes from a separate compatible vine rather than another runner from the same patch.'],
      },
      {
        id: 'second-maypop-vine',
        name: 'Second maypop vine',
        fruitType: 'passion-fruit',
        speciesGroup: 'incarnata',
        speciesGroupLabel: 'Maypop - second distinct vine',
        selfFertility: 'partially-self-fertile',
        bloomGroup: 'mid-late',
        bloomLabel: 'mid-late bloom',
        bloomOrder: 4,
        zoneRange: { min: 5, max: 9 },
        notes: ['Use this as the comparison partner when you are planning a separate maypop plant, seedling, or source for cross-pollination.'],
      },
    ],
  },
  serviceberry: {
    id: 'serviceberry',
    label: 'Serviceberry',
    description: 'Serviceberries can fruit as single plants, but two named cultivars usually give heavier crops and more even berry fill. They are especially rewarding when grown for fruit rather than just landscape use.',
    partnerDistanceNote: 'Keep serviceberries close enough for early pollinators to move between them during bloom. A small orchard row or paired planting works well.',
    minimumSetup: 'One serviceberry can fruit, but two cultivars are a better plan if harvest matters.',
    idealSetup: 'Plant two to three fruiting serviceberry cultivars with overlapping bloom for heavier crops and better berry size.',
    fallbackPlan: 'If you already have one ornamental-type serviceberry, add a fruiting cultivar before deciding the species is a poor producer.',
    varieties: [
      {
        id: 'rainbow-pillar',
        name: 'Rainbow Pillar',
        fruitType: 'serviceberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'early',
        bloomLabel: 'early bloom',
        bloomOrder: 2,
        zoneRange: { min: 4, max: 8 },
        notes: ['Often sold for its upright habit first, but it can still serve as a second serviceberry when bloom overlaps.'],
      },
      {
        id: 'thiessen',
        name: 'Thiessen',
        fruitType: 'serviceberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'early',
        bloomLabel: 'early bloom',
        bloomOrder: 2,
        zoneRange: { min: 3, max: 8 },
      },
      {
        id: 'smoky',
        name: 'Smoky',
        fruitType: 'serviceberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'early',
        bloomLabel: 'early bloom',
        bloomOrder: 2,
        zoneRange: { min: 3, max: 8 },
      },
      {
        id: 'autumn-brilliance',
        name: 'Autumn Brilliance',
        fruitType: 'serviceberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'early',
        bloomLabel: 'early bloom',
        bloomOrder: 2,
        zoneRange: { min: 4, max: 8 },
        notes: ['Commonly grown as an ornamental serviceberry, but it still fits as a practical flowering partner in mixed serviceberry plantings.'],
      },
      {
        id: 'northline',
        name: 'Northline',
        fruitType: 'serviceberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'early',
        bloomLabel: 'early bloom',
        bloomOrder: 2,
        zoneRange: { min: 2, max: 7 },
      },
      {
        id: 'regent',
        name: 'Regent',
        fruitType: 'serviceberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'early',
        bloomLabel: 'early bloom',
        bloomOrder: 2,
        zoneRange: { min: 2, max: 8 },
      },
      {
        id: 'honeywood',
        name: 'Honeywood',
        fruitType: 'serviceberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 3, max: 8 },
      },
      {
        id: 'martin',
        name: 'Martin',
        fruitType: 'serviceberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 3, max: 8 },
      },
    ],
  },
  mandarin: {
    id: 'mandarin',
    label: 'Mandarin',
    description: 'Most mandarins are self-fruitful, so one healthy grafted tree can usually set fruit. A second variety is optional for harvest variety, but nearby citrus can increase seediness in some otherwise low-seed types.',
    partnerDistanceNote: 'Mandarins do not usually need a partner tree. If you grow several citrus varieties, keep them close enough for bees to visit but expect some types to produce more seeds when cross-pollinated.',
    minimumSetup: 'One grafted mandarin is usually enough for fruit.',
    idealSetup: 'Choose one or two varieties with different ripening seasons if you want a longer winter citrus harvest, not because pollination requires it.',
    fallbackPlan: 'If a mandarin blooms but does not set fruit, first check tree age, cold damage, watering, nutrition, and bee activity before adding another citrus.',
    varieties: [
      {
        id: 'satsuma',
        name: 'Satsuma',
        fruitType: 'mandarin',
        selfFertility: 'self-fertile',
        bloomGroup: 'early-mid',
        bloomLabel: 'early-mid bloom',
        bloomOrder: 2,
        zoneRange: { min: 8, max: 11 },
        notes: ['One of the more cold-tolerant mandarins and usually seedless or low-seeded when grown away from compatible pollen.'],
      },
      {
        id: 'clementine',
        name: 'Clementine',
        fruitType: 'mandarin',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 9, max: 11 },
        notes: ['Can produce more seeds when compatible citrus blooms nearby.'],
      },
      {
        id: 'kishu',
        name: 'Kishu',
        fruitType: 'mandarin',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 9, max: 11 },
      },
      {
        id: 'gold-nugget',
        name: 'Gold Nugget',
        fruitType: 'mandarin',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid-late',
        bloomLabel: 'mid-late bloom',
        bloomOrder: 4,
        zoneRange: { min: 9, max: 11 },
      },
      {
        id: 'murcott-honey',
        name: 'Murcott / Honey',
        aliases: ['Honey Mandarin', 'Murcott'],
        fruitType: 'mandarin',
        selfFertility: 'self-fertile',
        bloomGroup: 'late',
        bloomLabel: 'late bloom',
        bloomOrder: 5,
        zoneRange: { min: 9, max: 11 },
        notes: ['Often benefits from thinning after heavy bloom to reduce alternate bearing.'],
      },
    ],
  },
  tangerine: {
    id: 'tangerine',
    label: 'Tangerine',
    description: 'Tangerines overlap with mandarins in practical orchard use. Most are self-fruitful, but crop quality, seediness, and season length vary by cultivar.',
    partnerDistanceNote: 'A partner tree is usually optional. If seedless fruit matters, avoid planting heavily cross-compatible citrus varieties right beside low-seed tangerines.',
    minimumSetup: 'One grafted tangerine can usually fruit by itself.',
    idealSetup: 'Plant more than one variety only if you want different flavors or a longer harvest window.',
    fallbackPlan: 'If flowers drop without fruit, check cold snaps, drought stress, immature tree age, and nutrient balance before assuming pollination failure.',
    varieties: [
      {
        id: 'dancy',
        name: 'Dancy',
        fruitType: 'tangerine',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 9, max: 11 },
      },
      {
        id: 'sunburst',
        name: 'Sunburst',
        fruitType: 'tangerine',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 9, max: 11 },
      },
      {
        id: 'pixie',
        name: 'Pixie',
        fruitType: 'tangerine',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid-late',
        bloomLabel: 'mid-late bloom',
        bloomOrder: 4,
        zoneRange: { min: 9, max: 11 },
      },
      {
        id: 'page',
        name: 'Page',
        fruitType: 'tangerine',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 9, max: 11 },
        notes: ['A tangelo-type mandarin hybrid with strong flavor.'],
      },
      {
        id: 'minneola',
        name: 'Minneola',
        fruitType: 'tangerine',
        selfFertility: 'partially-self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 9, max: 11 },
        notes: ['Often sets better with a compatible citrus pollen source, but that can increase seed count nearby.'],
      },
    ],
  },
  kumquat: {
    id: 'kumquat',
    label: 'Kumquat',
    description: 'Kumquats are self-fruitful and usually crop well as single trees. They are useful in the helper mostly for comparing varieties and planning cold-hardy container citrus.',
    partnerDistanceNote: 'No separate pollinizer is normally needed. Good bee activity during bloom still helps fruit set.',
    minimumSetup: 'One grafted kumquat is usually enough.',
    idealSetup: 'Choose a second variety only for different fruit shape, flavor, or harvest season.',
    fallbackPlan: 'If fruit set is poor, check container root health, cold damage, dryness during bloom, and scale insects before adding another tree.',
    varieties: [
      {
        id: 'nagami',
        name: 'Nagami',
        fruitType: 'kumquat',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 8, max: 11 },
      },
      {
        id: 'meiwa',
        name: 'Meiwa',
        fruitType: 'kumquat',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 8, max: 11 },
      },
      {
        id: 'fukushu',
        name: 'Fukushu',
        fruitType: 'kumquat',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 8, max: 11 },
      },
      {
        id: 'marumi',
        name: 'Marumi',
        fruitType: 'kumquat',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 8, max: 11 },
      },
      {
        id: 'centennial-variegated',
        name: 'Centennial Variegated',
        fruitType: 'kumquat',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 8, max: 11 },
      },
    ],
  },
  calamondin: {
    id: 'calamondin',
    label: 'Calamondin',
    description: 'Calamondin is self-fruitful and dependable in containers, often flowering and fruiting without another citrus nearby.',
    partnerDistanceNote: 'A partner tree is not normally needed. Indoor or screened plants may need gentle hand-shaking or access to insects if fruit set is poor.',
    minimumSetup: 'One grafted calamondin is usually enough.',
    idealSetup: 'Grow one healthy tree in strong light; add other citrus only for variety, not pollination.',
    fallbackPlan: 'If an indoor calamondin blooms but drops fruitlets, improve light, even watering, and humidity before changing pollination setup.',
    varieties: [
      {
        id: 'standard-calamondin',
        name: 'Standard Calamondin',
        fruitType: 'calamondin',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 9, max: 11 },
      },
      {
        id: 'variegated-calamondin',
        name: 'Variegated Calamondin',
        fruitType: 'calamondin',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 9, max: 11 },
      },
      {
        id: 'peters',
        name: 'Peters',
        fruitType: 'calamondin',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 9, max: 11 },
      },
      {
        id: 'dwarf-calamondin',
        name: 'Dwarf Calamondin',
        fruitType: 'calamondin',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 9, max: 11 },
      },
    ],
  },
  citron: {
    id: 'citron',
    label: 'Citron',
    description: 'Citron is usually self-fruitful, but it is grown mainly for aromatic rind rather than juice. Pollination is rarely the limiting issue compared with warmth, drainage, and frost protection.',
    partnerDistanceNote: 'A second citron is optional and usually unnecessary for home fruit set.',
    minimumSetup: 'One grafted citron can usually fruit by itself.',
    idealSetup: 'Choose a variety for its rind use, shape, or ritual needs, then focus on warm protected culture.',
    fallbackPlan: 'If a citron flowers but does not mature fruit, check cold stress, root health, and container watering before adding another tree.',
    varieties: [
      {
        id: 'etrog',
        name: 'Etrog',
        fruitType: 'citron',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 9, max: 11 },
      },
      {
        id: 'buddhas-hand',
        name: "Buddha's Hand",
        fruitType: 'citron',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 9, max: 11 },
      },
      {
        id: 'diamante',
        name: 'Diamante',
        fruitType: 'citron',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 9, max: 11 },
      },
      {
        id: 'corsican',
        name: 'Corsican',
        fruitType: 'citron',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 9, max: 11 },
      },
    ],
  },
  quince: {
    id: 'quince',
    label: 'Quince',
    description: 'Quince is usually self-fruitful, but a second quince often improves yield and gives you a backup if spring weather is poor.',
    partnerDistanceNote: 'Quince pollination is easiest when two trees are close enough for bees to cover both during the same bloom window.',
    minimumSetup: 'One quince is usually enough to get fruit.',
    idealSetup: 'Plant two quince cultivars if you want better yield, a stronger backup, and more flexibility around bloom weather.',
    fallbackPlan: 'If one quince is already established, add a second only if you want to push yield rather than simply get fruit.',
    varieties: [
      {
        id: 'orange',
        name: 'Orange',
        fruitType: 'quince',
        selfFertility: 'self-fertile',
        bloomGroup: 'late',
        bloomLabel: 'late bloom',
        bloomOrder: 4,
        zoneRange: { min: 5, max: 9 },
        chillHours: 'About 300 chill hours',
        chillBand: 'low',
      },
      {
        id: 'smyrna',
        name: 'Smyrna',
        fruitType: 'quince',
        selfFertility: 'self-fertile',
        bloomGroup: 'late',
        bloomLabel: 'late bloom',
        bloomOrder: 4,
        zoneRange: { min: 5, max: 9 },
        chillHours: 'About 300 chill hours',
        chillBand: 'low',
      },
      {
        id: 'pineapple',
        name: 'Pineapple',
        fruitType: 'quince',
        selfFertility: 'self-fertile',
        bloomGroup: 'late',
        bloomLabel: 'late bloom',
        bloomOrder: 4,
        zoneRange: { min: 5, max: 9 },
        chillHours: 'About 300 chill hours',
        chillBand: 'low',
      },
      {
        id: 'aromatnaya',
        name: 'Aromatnaya',
        fruitType: 'quince',
        selfFertility: 'self-fertile',
        bloomGroup: 'late',
        bloomLabel: 'late bloom',
        bloomOrder: 4,
        zoneRange: { min: 4, max: 9 },
        chillHours: 'About 300 chill hours',
        chillBand: 'low',
      },
      {
        id: 'champion',
        name: 'Champion',
        fruitType: 'quince',
        selfFertility: 'self-fertile',
        bloomGroup: 'late',
        bloomLabel: 'late bloom',
        bloomOrder: 4,
        zoneRange: { min: 5, max: 9 },
        chillHours: 'About 300 chill hours',
        chillBand: 'low',
      },
      {
        id: 'vranja',
        name: 'Vranja',
        fruitType: 'quince',
        selfFertility: 'self-fertile',
        bloomGroup: 'late',
        bloomLabel: 'late bloom',
        bloomOrder: 4,
        zoneRange: { min: 5, max: 9 },
        chillHours: 'About 300 chill hours',
        chillBand: 'low',
      },
      {
        id: 'meechs-prolific',
        name: "Meech's Prolific",
        fruitType: 'quince',
        selfFertility: 'self-fertile',
        bloomGroup: 'late',
        bloomLabel: 'late bloom',
        bloomOrder: 4,
        zoneRange: { min: 5, max: 9 },
        chillHours: 'About 300 chill hours',
        chillBand: 'low',
      },
    ],
  },
  kiwi: {
    id: 'kiwi',
    label: 'Kiwi',
    description: 'Most regular kiwis need both a fruiting female vine and a compatible male pollinizer. A self-fruitful type such as Jenny can crop alone, but even then a second vine often improves the harvest.',
    partnerDistanceNote: 'Keep male and fruiting kiwi vines on the same pergola, trellis row, or nearby support so pollinators can move easily between open flowers.',
    minimumSetup: 'Plant one fruiting kiwi plus one compatible male pollinizer, unless you are growing a self-fruitful type such as Jenny.',
    idealSetup: 'Grow one male kiwi for several fruiting females, or pair a self-fruitful kiwi with another compatible vine for stronger crops.',
    fallbackPlan: 'If you have a healthy kiwi vine with flowers but poor fruit set, add a compatible male pollinizer before assuming the vine is the problem.',
    varieties: [
      {
        id: 'hayward',
        name: 'Hayward',
        fruitType: 'kiwi',
        pollinationRole: 'fruiting',
        speciesGroup: 'deliciosa',
        speciesGroupLabel: 'Kiwi - fruiting vine',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 7, max: 9 },
      },
      {
        id: 'bruno',
        name: 'Bruno',
        fruitType: 'kiwi',
        pollinationRole: 'fruiting',
        speciesGroup: 'deliciosa',
        speciesGroupLabel: 'Kiwi - fruiting vine',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 7, max: 9 },
      },
      {
        id: 'jenny',
        name: 'Jenny',
        fruitType: 'kiwi',
        pollinationRole: 'fruiting',
        speciesGroup: 'deliciosa',
        speciesGroupLabel: 'Kiwi - self-fruitful vine',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 7, max: 9 },
        notes: ['Can fruit alone, but another compatible kiwi nearby often improves set and total yield.'],
      },
      {
        id: 'tomuri-male',
        name: 'Tomuri Male',
        fruitType: 'kiwi',
        pollinationRole: 'male-pollinizer',
        speciesGroup: 'deliciosa',
        speciesGroupLabel: 'Kiwi - male pollinizer',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 7, max: 9 },
        notes: ['Male pollinizer. This vine is grown to supply pollen, not fruit.'],
      },
      {
        id: 'matua-male',
        name: 'Matua Male',
        fruitType: 'kiwi',
        pollinationRole: 'male-pollinizer',
        speciesGroup: 'deliciosa',
        speciesGroupLabel: 'Kiwi - male pollinizer',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 7, max: 9 },
        notes: ['Male pollinizer. This vine is grown to supply pollen, not fruit.'],
      },
    ],
  },
  'hardy-kiwi': {
    id: 'hardy-kiwi',
    label: 'Hardy Kiwi',
    description: 'Most hardy kiwi vines need both a fruiting female vine and a compatible male pollinizer. Issai is the main easier exception, though even it often crops better with a male nearby.',
    partnerDistanceNote: 'Keep male and fruiting vines close together on the same trellis row or nearby supports so bloom overlap is easy to use.',
    minimumSetup: 'Plant one fruiting hardy kiwi plus one compatible male pollinizer, unless you are choosing a self-fruitful type such as Issai.',
    idealSetup: 'Grow one male hardy kiwi for several fruiting females, or pair Issai with another compatible kiwi for stronger crops.',
    fallbackPlan: 'If you have a fruiting kiwi vine with no partner, add a compatible male pollinizer before blaming the vine for poor fruit set.',
    varieties: [
      {
        id: 'ananasnaya',
        name: 'Ananasnaya',
        fruitType: 'hardy-kiwi',
        pollinationRole: 'fruiting',
        speciesGroup: 'arguta',
        speciesGroupLabel: 'Hardy kiwi - fruiting vine',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 4, max: 8 },
      },
      {
        id: 'kens-red',
        name: "Ken's Red",
        aliases: ['Kens Red', 'Ken'],
        fruitType: 'hardy-kiwi',
        pollinationRole: 'fruiting',
        speciesGroup: 'arguta',
        speciesGroupLabel: 'Hardy kiwi - fruiting vine',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 8 },
      },
      {
        id: 'geneva',
        name: 'Geneva',
        fruitType: 'hardy-kiwi',
        pollinationRole: 'fruiting',
        speciesGroup: 'arguta',
        speciesGroupLabel: 'Hardy kiwi - fruiting vine',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'mid-late',
        bloomLabel: 'mid-late bloom',
        bloomOrder: 4,
        zoneRange: { min: 5, max: 8 },
      },
      {
        id: 'issai',
        name: 'Issai',
        fruitType: 'hardy-kiwi',
        pollinationRole: 'fruiting',
        speciesGroup: 'arguta',
        speciesGroupLabel: 'Hardy kiwi - self-fruitful vine',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 6, max: 9 },
        notes: ['Can fruit alone, but another compatible kiwi nearby often improves the crop.'],
      },
      {
        id: 'meader-male',
        name: 'Meader Male',
        fruitType: 'hardy-kiwi',
        pollinationRole: 'male-pollinizer',
        speciesGroup: 'arguta',
        speciesGroupLabel: 'Hardy kiwi - male pollinizer',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 8 },
        notes: ['Male pollinizer. This vine is grown to supply pollen, not fruit.'],
      },
      {
        id: '74-49-male',
        name: '74-49 Male',
        fruitType: 'hardy-kiwi',
        pollinationRole: 'male-pollinizer',
        speciesGroup: 'arguta',
        speciesGroupLabel: 'Hardy kiwi - male pollinizer',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 4, max: 8 },
        notes: ['Male pollinizer. This vine is grown to supply pollen, not fruit.'],
      },
    ],
  },
  gooseberry: {
    id: 'gooseberry',
    label: 'Gooseberry',
    description: 'Gooseberries are usually self-fruitful, but another cultivar nearby can still improve yield and fruit size in home plantings.',
    partnerDistanceNote: 'Gooseberries flower early, so nearby companions and active pollinators help on cool spring days.',
    minimumSetup: 'One gooseberry plant is enough for fruit.',
    idealSetup: 'Plant two gooseberry cultivars if you want heavier crops and larger berries.',
    fallbackPlan: 'If a single gooseberry is fruiting lightly, improve sun, pruning, and soil moisture first, then consider adding a second cultivar.',
    varieties: [
      {
        id: 'captivator',
        name: 'Captivator',
        fruitType: 'gooseberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'early',
        bloomLabel: 'early bloom',
        bloomOrder: 2,
        zoneRange: { min: 3, max: 8 },
      },
      {
        id: 'hinnomaki-red',
        name: 'Hinnonmaki Red',
        aliases: ['Hinnonmäki Red'],
        fruitType: 'gooseberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'early',
        bloomLabel: 'early bloom',
        bloomOrder: 2,
        zoneRange: { min: 3, max: 8 },
      },
      {
        id: 'invicta',
        name: 'Invicta',
        fruitType: 'gooseberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'early',
        bloomLabel: 'early bloom',
        bloomOrder: 2,
        zoneRange: { min: 3, max: 8 },
      },
      {
        id: 'jeanne',
        name: 'Jeanne',
        fruitType: 'gooseberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 3, max: 8 },
      },
      {
        id: 'poorman',
        name: 'Poorman',
        fruitType: 'gooseberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 3, max: 8 },
      },
      {
        id: 'pixwell',
        name: 'Pixwell',
        fruitType: 'gooseberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 3, max: 8 },
      },
      {
        id: 'whinhams-industry',
        name: "Whinham's Industry",
        fruitType: 'gooseberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 4, max: 8 },
      },
    ],
  },
  currant: {
    id: 'currant',
    label: 'Currant',
    description: 'Many currants can fruit alone, but a second cultivar often improves yield. Red and white currants are usually easiest. Black currants are best matched with other black currants.',
    partnerDistanceNote: 'Currants are small shrubs, so keeping them in the same bed or nearby rows makes pollination easy.',
    minimumSetup: 'One currant can fruit, but two cultivars are the better plan if you want stronger harvests.',
    idealSetup: 'Plant two or more currants from the same currant group so bloom overlap and fruit set stay reliable.',
    fallbackPlan: 'If you only have one currant, add a second cultivar from the same currant group before assuming the first plant is unproductive.',
    varieties: [
      {
        id: 'red-lake',
        name: 'Red Lake',
        fruitType: 'currant',
        speciesGroup: 'red-white',
        speciesGroupLabel: 'Red or white currant',
        selfFertility: 'self-fertile',
        bloomGroup: 'early',
        bloomLabel: 'early bloom',
        bloomOrder: 2,
        zoneRange: { min: 3, max: 7 },
      },
      {
        id: 'jonkheer-van-tets',
        name: 'Jonkheer van Tets',
        fruitType: 'currant',
        speciesGroup: 'red-white',
        speciesGroupLabel: 'Red or white currant',
        selfFertility: 'self-fertile',
        bloomGroup: 'early',
        bloomLabel: 'early bloom',
        bloomOrder: 2,
        zoneRange: { min: 4, max: 8 },
      },
      {
        id: 'rovada',
        name: 'Rovada',
        fruitType: 'currant',
        speciesGroup: 'red-white',
        speciesGroupLabel: 'Red or white currant',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 4, max: 8 },
      },
      {
        id: 'pink-champagne',
        name: 'Pink Champagne',
        fruitType: 'currant',
        speciesGroup: 'red-white',
        speciesGroupLabel: 'Red or white currant',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 4, max: 8 },
      },
      {
        id: 'white-imperial',
        name: 'White Imperial',
        fruitType: 'currant',
        speciesGroup: 'red-white',
        speciesGroupLabel: 'Red or white currant',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 3, max: 7 },
      },
      {
        id: 'consort',
        name: 'Consort',
        fruitType: 'currant',
        speciesGroup: 'black',
        speciesGroupLabel: 'Black currant',
        selfFertility: 'self-fertile',
        bloomGroup: 'early',
        bloomLabel: 'early bloom',
        bloomOrder: 2,
        zoneRange: { min: 3, max: 7 },
      },
      {
        id: 'crandall',
        name: 'Crandall',
        fruitType: 'currant',
        speciesGroup: 'black',
        speciesGroupLabel: 'Black currant',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 4, max: 8 },
        notes: ['Often sold as clove currant. Same-group black currant matches are still the safer pollination choice.'],
      },
      {
        id: 'titania',
        name: 'Titania',
        fruitType: 'currant',
        speciesGroup: 'black',
        speciesGroupLabel: 'Black currant',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 3, max: 7 },
      },
      {
        id: 'ben-sarek',
        name: 'Ben Sarek',
        fruitType: 'currant',
        speciesGroup: 'black',
        speciesGroupLabel: 'Black currant',
        selfFertility: 'self-fertile',
        bloomGroup: 'early-mid',
        bloomLabel: 'early-mid bloom',
        bloomOrder: 2,
        zoneRange: { min: 3, max: 7 },
      },
    ],
  },
  jujube: {
    id: 'jujube',
    label: 'Jujube',
    description: 'Most jujubes can fruit alone, but having another cultivar nearby often improves fruit set, size, and consistency. Mixed plantings are usually the safer choice.',
    partnerDistanceNote: 'Jujubes are best planted close enough for insects to work several trees in the same bloom window. In a home orchard, nearby spacing is usually easy.',
    minimumSetup: 'One self-fruitful jujube can work, but two cultivars are a better long-term plan.',
    idealSetup: 'Plant two or more jujube cultivars with overlapping bloom for stronger fruit set and better fruit quality.',
    fallbackPlan: 'If you already planted one jujube, add a second cultivar before deciding that the species is shy-bearing in your climate.',
    varieties: [
      {
        id: 'li',
        name: 'Li',
        fruitType: 'jujube',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 6, max: 10 },
      },
      {
        id: 'lang',
        name: 'Lang',
        fruitType: 'jujube',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 6, max: 10 },
      },
      {
        id: 'sugar-cane',
        name: 'Sugar Cane',
        fruitType: 'jujube',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 6, max: 10 },
      },
      {
        id: 'honey-jar',
        name: 'Honey Jar',
        fruitType: 'jujube',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid-early',
        bloomLabel: 'early-mid bloom',
        bloomOrder: 2,
        zoneRange: { min: 6, max: 10 },
      },
      {
        id: 'shanxi-li',
        name: 'Shanxi Li',
        fruitType: 'jujube',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid-late',
        bloomLabel: 'mid-late bloom',
        bloomOrder: 4,
        zoneRange: { min: 6, max: 10 },
      },
      {
        id: 'chico',
        name: 'Chico',
        fruitType: 'jujube',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 6, max: 10 },
      },
      {
        id: 'ga-866',
        name: 'GA-866',
        fruitType: 'jujube',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid-late',
        bloomLabel: 'mid-late bloom',
        bloomOrder: 4,
        zoneRange: { min: 6, max: 10 },
      },
      {
        id: 'sherwood',
        name: 'Sherwood',
        fruitType: 'jujube',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid-late',
        bloomLabel: 'mid-late bloom',
        bloomOrder: 4,
        zoneRange: { min: 6, max: 10 },
      },
      {
        id: 'so',
        name: 'So',
        fruitType: 'jujube',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 6, max: 10 },
      },
    ],
  },
  honeyberry: {
    id: 'honeyberry',
    label: 'Honeyberry',
    description: 'Honeyberries are not self-fertile and require two genetically distinct varieties blooming at the same time. Without a compatible partner, plants flower reliably but produce little to no fruit. Because honeyberry blooms very early in spring - often before honeybees are active - bumblebees and native early pollinators are the primary pollinators.',
    partnerDistanceNote: 'Plant honeyberry partners within 6 to 8 feet of each other so early bumblebees can easily move between shrubs during the brief bloom window.',
    minimumSetup: 'Plant at least two different honeyberry varieties with overlapping bloom. A single plant will not fruit reliably.',
    idealSetup: 'Plant three or more varieties with staggered but overlapping bloom so pollination stays reliable even in cold or wet springs.',
    fallbackPlan: 'If a lone honeyberry is flowering but not fruiting, adding a second variety with overlapping bloom nearby is almost always the fix.',
    varieties: [
      {
        id: 'pirika',
        name: 'Pirika',
        fruitType: 'honeyberry',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'late',
        bloomLabel: 'late bloom',
        bloomOrder: 4,
        zoneRange: { min: 4, max: 8 },
        researchNotes: ['Thompson/OSU Japanese haskap; late-blooming variety per plant patent (PP28954) and HoneyberryUSA bloom chart - within the Thompson series Pirika is the earliest bloomer, but all Thompson varieties bloom late relative to Russian/USask types; pairs with Keiko, Taka, and Tana; patent confirms hardiness to at least zone 4'],
      },
      {
        id: 'borealis',
        name: 'Borealis',
        fruitType: 'honeyberry',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 2, max: 7 },
        researchNotes: ['University of Saskatchewan variety; mid-season bloomer per HoneyberryUSA bloom chart - bloom can start with the early group but peaks mid-season; pairs well with Aurora, Honeybee, or Berry Blue; large, very sweet, fragile berries best for fresh eating'],
      },
      {
        id: 'tundra',
        name: 'Tundra',
        fruitType: 'honeyberry',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'early',
        bloomLabel: 'early bloom',
        bloomOrder: 2,
        zoneRange: { min: 2, max: 7 },
        researchNotes: ['University of Saskatchewan variety; early-blooming with one of the widest bloom windows - HoneyberryUSA confirms approximately 80% bloom overlap with Boreal Blizzard and full overlap with Indigo series; firm, tart-to-sweet berries suited to mechanical harvest; pairs well with Aurora, Honeybee, Berry Blue, and Indigo series'],
      },
      {
        id: 'boreal-blizzard',
        name: 'Boreal Blizzard',
        fruitType: 'honeyberry',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 2, max: 7 },
        incompatibleVarietyIds: ['aurora', 'boreal-beauty'],
        researchNotes: ['University of Saskatchewan Boreal series variety; blooms mid-season, later than the original early USask types but with approximately 80% bloom overlap with Tundra and Indigo series per HoneyberryUSA; consistently poor fruit set when paired with Aurora (confirmed by USask controlled crosses); poor cross-pollination with Boreal Beauty due to bloom timing gap; use Boreal Beast as the primary companion; very large, sweet berries'],
      },
      {
        id: 'indigo-gem',
        name: 'Indigo Gem',
        fruitType: 'honeyberry',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'early',
        bloomLabel: 'early bloom',
        bloomOrder: 2,
        zoneRange: { min: 2, max: 7 },
        researchNotes: ['University of Saskatchewan variety; early-blooming sibling of Indigo Treat and Indigo Yum; most productive of the Indigo series; pairs well with Tundra, Aurora, Honeybee, or Berry Blue'],
      },
      {
        id: 'indigo-treat',
        name: 'Indigo Treat',
        fruitType: 'honeyberry',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'early',
        bloomLabel: 'early bloom',
        bloomOrder: 2,
        zoneRange: { min: 2, max: 7 },
        researchNotes: ['University of Saskatchewan variety; early-blooming sibling of Indigo Gem and Indigo Yum; similar to Tundra but with slightly smaller berries; pairs well with Tundra, Aurora, Honeybee, or Berry Blue'],
      },
      {
        id: 'keiko',
        name: 'Keiko',
        fruitType: 'honeyberry',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'late',
        bloomLabel: 'late bloom',
        bloomOrder: 4,
        zoneRange: { min: 3, max: 7 },
        researchNotes: ['Thompson/OSU Japanese haskap; late-blooming variety - pairs with Tana or other late-season companions; medium-large oval berries with balanced sweet-tart flavor and excellent shelf life; note: Pirika is listed as a Thompson variety that pairs with Keiko but its bloom group needs verification - the two may no longer be compatible at these assignments'],
      },
      {
        id: 'berry-blue',
        name: 'Berry Blue',
        aliases: ['Berry Smart Blue', 'Czech #17'],
        fruitType: 'honeyberry',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'early',
        bloomLabel: 'early bloom',
        bloomOrder: 2,
        zoneRange: { min: 2, max: 7 },
        researchNotes: ['Russian/Czech origin; compact early bloomer widely used as a pollinizer for USask varieties; compatible with Borealis, Tundra, Indigo series, and Honeybee'],
      },
      {
        id: 'blue-forest',
        name: 'Blue Forest',
        fruitType: 'honeyberry',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'early',
        bloomLabel: 'early bloom',
        bloomOrder: 2,
        zoneRange: { min: 3, max: 7 },
        researchNotes: ['Russian/Magadan origin (also listed as Blue Forest or Magadan in some catalogs); early-blooming variety per HoneyberryUSA bloom chart - placed in the early/short-bloom Russian category alongside Tundra and Indigo series; some PNW nurseries classify it as late in their regional 2-category system, but HoneyberryUSA\'s continental 5-group classification is authoritative for this app; pairs with Borealis, Tundra, Indigo series, and other early Russian types; compact spreading form 2-3 ft. with ornamental foliage'],
      },
      {
        id: 'honeybee',
        name: 'Honeybee',
        fruitType: 'honeyberry',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 2, max: 7 },
        researchNotes: ['University of Saskatchewan variety bred specifically as a pollinizer; mid-season bloom overlaps all early USask types (Borealis, Tundra, Indigo series) and Aurora; cylindrical tart-to-sweet berries with good holding ability'],
      },
      {
        id: 'aurora',
        name: 'Aurora',
        fruitType: 'honeyberry',
        selfFertility: 'partially-self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 2, max: 7 },
        incompatibleVarietyIds: ['boreal-blizzard'],
        researchNotes: ['University of Saskatchewan variety; partially self-fertile; large, deep-flavored, chewy berries; excellent cross-pollinator for most USask types but shows poor fruit set with Boreal Blizzard - use Honeybee or Boreal Beast for Blizzard instead'],
      },
      {
        id: 'indigo-yum',
        name: 'Indigo Yum',
        fruitType: 'honeyberry',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'early',
        bloomLabel: 'early bloom',
        bloomOrder: 2,
        zoneRange: { min: 2, max: 7 },
        researchNotes: ['University of Saskatchewan variety; early-blooming sibling of Indigo Gem and Indigo Treat - same bloom group as the Indigo series per HoneyberryUSA; largest-fruited of the Indigo series; upright vigorous growth to 4 ft.; pairs well with Tundra, Aurora, Honeybee, or Berry Blue'],
      },
      {
        id: 'boreal-beast',
        name: 'Boreal Beast',
        fruitType: 'honeyberry',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 2, max: 7 },
        researchNotes: ['University of Saskatchewan variety; acts as bridge pollinizer - bloom overlaps with both Boreal Blizzard and Boreal Beauty; honey-blueberry-raspberry flavored berries with good sweetness-acidity balance'],
      },
      {
        id: 'blue-banana',
        name: 'Blue Banana',
        fruitType: 'honeyberry',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 2, max: 7 },
        researchNotes: ['Berries Unlimited variety; very large, juicy, sweet berries with smoked-strawberry flavor notes; pairs with Happy Giant or Blue Moose; bloom timing is based on the breeder\'s own descriptions and has not been independently verified'],
      },
      {
        id: 'blue-moose',
        name: 'Blue Moose',
        fruitType: 'honeyberry',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 2, max: 7 },
        researchNotes: ['Berries Unlimited variety; vigorous dome-shaped shrub to 5 ft.; large, chewy, sweet berries; pairs with Happy Giant or Blue Banana; bloom timing is based on the breeder\'s own descriptions and has not been independently verified'],
      },
      {
        id: 'happy-giant',
        name: 'Happy Giant',
        fruitType: 'honeyberry',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 2, max: 7 },
        researchNotes: ['Berries Unlimited variety; very large berries (over 1 inch); sweet with light tartness; vigorous dome habit; pairs with Blue Moose or Blue Banana; bloom timing is based on the breeder\'s own descriptions and has not been independently verified'],
      },
      {
        id: 'taka',
        name: 'Taka',
        fruitType: 'honeyberry',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'late',
        bloomLabel: 'late bloom',
        bloomOrder: 4,
        zoneRange: { min: 3, max: 7 },
        researchNotes: ['Thompson/OSU Japanese haskap; late-blooming variety per HoneyberryUSA bloom chart and plant patent PP26707 (50% anthesis April 12 in Corvallis, OR) - within the Thompson series, Taka blooms between the earliest (Pirika) and latest (Tana) types, but all Thompson varieties bloom late relative to Russian/USask types; cylindrical medium-large berries with mild, sweet flavor; pairs with Keiko and Tana'],
      },
      {
        id: 'boreal-beauty',
        name: 'Boreal Beauty',
        fruitType: 'honeyberry',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'late',
        bloomLabel: 'late bloom',
        bloomOrder: 4,
        zoneRange: { min: 2, max: 7 },
        incompatibleVarietyIds: ['boreal-blizzard'],
        researchNotes: ['University of Saskatchewan variety; latest of the Boreal series - ripens a full month after most early varieties; poor cross-pollination with Boreal Blizzard confirmed by USask research (bloom timing gap - Blizzard finishes before Beauty peaks); Boreal Beast is the designated companion as it bridges both Blizzard and Beauty; very large, sweet berries'],
      },
      {
        id: 'blue-hokkaido',
        name: 'Blue Hokkaido',
        fruitType: 'honeyberry',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'late',
        bloomLabel: 'late bloom',
        bloomOrder: 4,
        zoneRange: { min: 3, max: 7 },
        researchNotes: ['Japanese variety; upright 4 to 5 ft. shrub with very large, crisp, sweet-tart berries; requires another late-blooming variety - bloom timing is 2 or more weeks after Russian types'],
      },
      {
        id: 'blue-mist',
        name: 'Blue Mist',
        fruitType: 'honeyberry',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'late',
        bloomLabel: 'late bloom',
        bloomOrder: 4,
        zoneRange: { min: 3, max: 7 },
        researchNotes: ['Compact spreading shrub (2 to 3 ft.) selected from Japanese seedlings; large, sweet-tart berries with velvety foliage; needs another late-blooming companion'],
      },
      {
        id: 'blue-pacific',
        name: 'Blue Pacific',
        fruitType: 'honeyberry',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'late',
        bloomLabel: 'late bloom',
        bloomOrder: 4,
        zoneRange: { min: 3, max: 7 },
        researchNotes: ['Kuril Islands origin; compact spreading shrub (2 to 3 ft.) with large, sweet, light-blue fruit; pairs with Blue Pagoda, Blue Velvet, or other late-blooming Kuril-type companions - do not pair with early Russian/Czech types like Berry Blue, which finish blooming before Kuril types open'],
      },
      {
        id: 'blue-pagoda',
        name: 'Blue Pagoda',
        fruitType: 'honeyberry',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'late',
        bloomLabel: 'late bloom',
        bloomOrder: 4,
        zoneRange: { min: 3, max: 7 },
        researchNotes: ['Kuril/Japanese origin; cold-hardy to zone 2; primarily used as a late pollinizer rather than for fruit quality; pairs with Blue Velvet or other late types'],
      },
      {
        id: 'giants-heart',
        name: "Giant's Heart",
        fruitType: 'honeyberry',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'late',
        bloomLabel: 'late bloom',
        bloomOrder: 4,
        zoneRange: { min: 3, max: 7 },
        researchNotes: ['Berries Unlimited variety; large, oval, heart-shaped berries with very sweet flavor and a distinctive smoked-prune finish; pairs with Strawberry Sensation or other late bloomers; bloom timing is based on the breeder\'s own descriptions and has not been independently verified'],
      },
      {
        id: 'strawberry-sensation',
        name: 'Strawberry Sensation',
        fruitType: 'honeyberry',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'late',
        bloomLabel: 'late bloom',
        bloomOrder: 4,
        zoneRange: { min: 2, max: 7 },
        researchNotes: ["Berries Unlimited variety; upright shrub (8 to 9 ft. at maturity); dark blue berries with sweet-tart flavor and distinctive strawberry finish; pairs with Giant's Heart or Boreal Beauty; bloom timing is based on the breeder's own descriptions and has not been independently verified"],
      },
      {
        id: 'blue-moon',
        name: 'Blue Moon',
        fruitType: 'honeyberry',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'very-late',
        bloomLabel: 'very late bloom',
        bloomOrder: 5,
        zoneRange: { min: 3, max: 7 },
        researchNotes: ['Kuril-type variety; fruit stays green inside even when fully ripe externally - a useful ripeness indicator; pairs with Blue Velvet or Blue Sea'],
      },
      {
        id: 'blue-sea',
        name: 'Blue Sea',
        fruitType: 'honeyberry',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'very-late',
        bloomLabel: 'very late bloom',
        bloomOrder: 5,
        zoneRange: { min: 3, max: 7 },
        researchNotes: ['Dwarf spreading plant (2 to 3 ft.) selected from Japanese seedlings; crisp, very large, sweet, flavorful fruit; among the latest-blooming honeyberry varieties'],
      },
      {
        id: 'blue-velvet',
        name: 'Blue Velvet',
        fruitType: 'honeyberry',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'very-late',
        bloomLabel: 'very late bloom',
        bloomOrder: 5,
        zoneRange: { min: 3, max: 7 },
        researchNotes: ['Ukrainian Kuril-type; distinctive grey-green velvety foliage; large, sweet, crisp fruit; among the latest-blooming varieties - pairs with Blue Moon or Blue Sea'],
      },
      {
        id: 'tana',
        name: 'Tana',
        fruitType: 'honeyberry',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'late',
        bloomLabel: 'late bloom',
        bloomOrder: 4,
        zoneRange: { min: 3, max: 8 },
        researchNotes: ['Thompson/OSU Japanese haskap; latest-blooming Thompson variety; oval medium-large tart-sweet berries with outstanding shelf life (24+ days refrigerated); pairs with Taka (mid-season) or other late-blooming companions'],
      },
      {
        id: 'blue-horn',
        name: 'Blue Horn',
        fruitType: 'honeyberry',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 3, max: 7 },
        researchNotes: ['Limited published data available for this variety; bloom timing and zone range are approximate - confirm specifics with your supplier before pairing'],
      },
    ],
  },
  hazelnut: {
    id: 'hazelnut',
    label: 'Hazelnut',
    description: 'Hazelnuts are wind-pollinated and self-incompatible. Two varieties with overlapping catkin shed and no shared dominant S-alleles are required for any crop. Even bloom-matched varieties can fail to pollinate each other if they share S-alleles - known incompatible pairs are flagged here. Most European OSU varieties carry resistance to Eastern Filbert Blight (EFB); traditional varieties like Barcelona and Ennis are susceptible and should be paired with resistant cultivars where EFB is present.',
    partnerDistanceNote: 'Plant hazelnut companions within 50 to 100 feet. Wind carries pollen beyond that range in open sites, but closer is more reliable.',
    minimumSetup: 'Two hazelnut varieties confirmed to be S-allele compatible with overlapping catkin bloom.',
    idealSetup: 'Three varieties covering early, mid, and late bloom so pollen is available throughout the full female-flower receptive window.',
    fallbackPlan: 'If an existing hazelnut produces no nuts despite flowering, the most common cause is an S-allele-mismatched companion. Replace or add a confirmed compatible variety.',
    varieties: [
      {
        id: 'yamhill',
        name: 'Yamhill',
        fruitType: 'hazelnut',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'very-early',
        bloomLabel: 'very early bloom',
        bloomOrder: 1,
        zoneRange: { min: 5, max: 8 },
        incompatibleVarietyIds: ['lewis', 'clark'],
        researchNotes: ['OSU EFB-resistant release (S8S26); earliest-blooming OSU variety; catkins shed mid-winter in the Pacific Northwest; Lewis and Clark both carry S8 in pollen - S-allele incompatible with Yamhill (S8S26); pairs with Gamma, York, or Sacajawea/Santiam instead'],
      },
      {
        id: 'daviana',
        name: 'Daviana',
        fruitType: 'hazelnut',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'very-early',
        bloomLabel: 'very early bloom',
        bloomOrder: 1,
        zoneRange: { min: 4, max: 8 },
        warnings: ['EFB-susceptible - largely replaced by resistant alternatives; only plant where Eastern Filbert Blight is absent'],
        researchNotes: ['Classic early-season pollinizer for Barcelona; now widely replaced by EFB-resistant alternatives'],
      },
      {
        id: 'barcelona',
        name: 'Barcelona',
        fruitType: 'hazelnut',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'early',
        bloomLabel: 'early bloom',
        bloomOrder: 2,
        zoneRange: { min: 4, max: 8 },
        warnings: ['EFB-susceptible - the backbone of traditional Pacific Northwest orchards but vulnerable to Eastern Filbert Blight without resistant companions'],
        researchNotes: ['Classic commercial variety; pairs with Daviana, Butler, or EFB-resistant OSU types; heavy reliable crops when healthy'],
      },
      {
        id: 'butler',
        name: 'Butler',
        fruitType: 'hazelnut',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'early',
        bloomLabel: 'early bloom',
        bloomOrder: 2,
        zoneRange: { min: 4, max: 8 },
        warnings: ['EFB-susceptible - valued as a pollinizer for Ennis and Barcelona but lacks EFB resistance'],
        researchNotes: ['Mid-quality fruiting variety; primary value is as an early pollinizer for Barcelona and Ennis'],
      },
      {
        id: 'the-beast',
        name: 'The Beast',
        fruitType: 'hazelnut',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'early',
        bloomLabel: 'early bloom',
        bloomOrder: 2,
        zoneRange: { min: 4, max: 8 },
        researchNotes: ['Rutgers/HHC/OSU Consortium hybrid (75% C. avellana, 25% C. americana); vigorous tree to 20 ft with strong EFB resistance; S-allele confirmed compatible with Grand Traverse, Geneva, and NITKA'],
      },
      {
        id: 'clark',
        name: 'Clark',
        fruitType: 'hazelnut',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'early',
        bloomLabel: 'early bloom',
        bloomOrder: 2,
        zoneRange: { min: 5, max: 8 },
        incompatibleVarietyIds: ['lewis', 'yamhill'],
        researchNotes: ['OSU EFB-resistant release (S3S8); small nuts, high yield, early harvest; S8 pollen is S-allele incompatible with Lewis (S3S8) and Yamhill (S8S26) - do not pair Clark with either of those two'],
      },
      {
        id: 'casina',
        name: 'Casina',
        fruitType: 'hazelnut',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'early',
        bloomLabel: 'early bloom',
        bloomOrder: 2,
        zoneRange: { min: 5, max: 8 },
        incompatibleVarietyIds: ['york'],
        warnings: ['Highly EFB-susceptible (PNW Pest Handbook: long cankers, low vigor); bloom timing data is conflicting across sources - verify local bloom overlap with your chosen partner before planting'],
        researchNotes: ['Spanish/Italian origin (S10S21); exceptional kernel-to-shell ratio; York pollen (S21) is S-allele incompatible with Casina (S10S21) per Mehlenbacher et al. 2018 - do not pair these two'],
      },
      {
        id: 'lewis',
        name: 'Lewis',
        fruitType: 'hazelnut',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 8 },
        incompatibleVarietyIds: ['clark', 'yamhill'],
        researchNotes: ['OSU EFB-resistant release (S3S8); mild-flavored medium nuts; S8 pollen is S-allele incompatible with Clark (S3S8) and Yamhill (S8S26) - do not pair Lewis with either of those two'],
      },
      {
        id: 'dorris',
        name: 'Dorris',
        fruitType: 'hazelnut',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 8 },
        researchNotes: ['OSU EFB-resistant release; compact tree (10 to 12 ft); pairs well with York or Felix'],
      },
      {
        id: 'york',
        name: 'York',
        fruitType: 'hazelnut',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 8 },
        incompatibleVarietyIds: ['casina'],
        researchNotes: ['OSU EFB-resistant release (S2S21); wide mid-season bloom makes it a good general pollinizer; S21 pollen is S-allele incompatible with Casina (S10S21) - do not pair these two; not suitable for Jefferson due to bloom timing mismatch (York pollen is mostly spent before Jefferson female flowers open), not an S-allele conflict'],
      },
      {
        id: 'wepster',
        name: 'Wepster',
        fruitType: 'hazelnut',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 8 },
        researchNotes: ['OSU EFB-resistant release; good kernel quality; OSU recommends McDonald (primary), York, and Felix as pollinizers - McDonald is a confirmed OSU release but is not in this catalog; use York or Felix as the companion'],
      },
      {
        id: 'gamma',
        name: 'Gamma',
        fruitType: 'hazelnut',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 8 },
        researchNotes: ['OSU EFB-resistant pollinizer release; recommended companion for Yamhill; mid-season catkin shed'],
      },
      {
        id: 'grand-traverse',
        name: 'Grand Traverse',
        fruitType: 'hazelnut',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 8 },
        researchNotes: ['75% C. avellana / 25% C. colurna (Turkish tree hazel) complex hybrid from Michigan, developed by amateur breeder Cecil Farris; S11 and S25 are rare alleles making it a near-universal pollinizer with very few S-allele conflicts; S-allele confirmed compatible with The Beast'],
      },
      {
        id: 'jefferson',
        name: 'Jefferson',
        fruitType: 'hazelnut',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'late',
        bloomLabel: 'late bloom',
        bloomOrder: 4,
        zoneRange: { min: 5, max: 8 },
        researchNotes: ['OSU flagship EFB-resistant variety (S1S3); female flowers receptive very late in the season - requires late-shedding pollinizers Felix (earliest of the three), Eta, or Theta; York pollen is not S-allele incompatible with Jefferson but sheds too early to overlap reliably with Jefferson female receptivity'],
      },
      {
        id: 'ennis',
        name: 'Ennis',
        fruitType: 'hazelnut',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'late',
        bloomLabel: 'late bloom',
        bloomOrder: 4,
        zoneRange: { min: 5, max: 9 },
        warnings: ['EFB-susceptible - produces the largest nuts of any common hazelnut but is highly vulnerable to Eastern Filbert Blight'],
        researchNotes: ['Large-nutted traditional variety; female bloom is late - requires Barcelona, Butler, or Hall\'s Giant as a pollinizer'],
      },
      {
        id: 'halls-giant',
        name: "Hall's Giant",
        fruitType: 'hazelnut',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'late',
        bloomLabel: 'late bloom',
        bloomOrder: 4,
        zoneRange: { min: 5, max: 8 },
        warnings: ['EFB-susceptible - one of the best late pollinizers for Ennis and Jefferson but lacks EFB resistance'],
        researchNotes: ['One of the best late-season pollinizers for Ennis; sheds large volumes of pollen late; OSU does not recommend Hall\'s Giant for Jefferson due to its EFB susceptibility even though S-allele compatibility exists - use Eta, Theta, or Felix for Jefferson instead'],
      },
      {
        id: 'eta',
        name: 'Eta',
        fruitType: 'hazelnut',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'late',
        bloomLabel: 'late bloom',
        bloomOrder: 4,
        zoneRange: { min: 5, max: 8 },
        researchNotes: ['OSU EFB-resistant pollinizer release; mid-to-late pollen shed; primary use is pollinating Jefferson alongside Theta'],
      },
      {
        id: 'felix',
        name: 'Felix',
        fruitType: 'hazelnut',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'late',
        bloomLabel: 'late bloom',
        bloomOrder: 4,
        zoneRange: { min: 5, max: 8 },
        researchNotes: ['OSU EFB-resistant release; earliest of Jefferson\'s three recommended pollinizers; pairs well with Dorris and Jefferson'],
      },
      {
        id: 'delta',
        name: 'Delta',
        fruitType: 'hazelnut',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'late',
        bloomLabel: 'late bloom',
        bloomOrder: 4,
        zoneRange: { min: 5, max: 8 },
        researchNotes: ['OSU EFB-resistant pollinizer release; compatible with Lewis, Clark, and Yamhill; mid-to-late bloom'],
      },
      {
        id: 'epsilon',
        name: 'Epsilon',
        fruitType: 'hazelnut',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'late',
        bloomLabel: 'late bloom',
        bloomOrder: 4,
        zoneRange: { min: 5, max: 8 },
        researchNotes: ['OSU EFB-resistant pollinizer release; covers mid-through-late female bloom of Lewis and Clark'],
      },
      {
        id: 'theta',
        name: 'Theta',
        fruitType: 'hazelnut',
        selfFertility: 'not-self-fertile',
        bloomGroup: 'very-late',
        bloomLabel: 'very late bloom',
        bloomOrder: 5,
        zoneRange: { min: 5, max: 8 },
        researchNotes: ['OSU EFB-resistant pollinizer release; latest pollen shed of all OSU varieties; essential for reliably pollinating Jefferson when female receptivity extends late'],
      },
    ],
  },
  raspberry: {
    id: 'raspberry',
    label: 'Raspberry',
    description: 'All raspberry varieties are self-fertile and will produce a full crop without a cross-pollinizer. Red and yellow raspberries are best paired with other red or yellow varieties; black raspberries (Rubus occidentalis) should be paired with other blacks since reds do not effectively cross-pollinate them. The most useful planning distinction is fruiting type: summer-bearing (floricane) varieties produce one intense mid-summer crop on second-year canes, while fall-bearing (primocane) varieties produce their main crop in late summer and fall on first-year canes and can also produce a lighter summer crop the following year.',
    partnerDistanceNote: 'Raspberries are insect-pollinated and self-fertile, so companion plants nearby boost pollinator activity rather than providing essential cross-pollination.',
    minimumSetup: 'One plant will produce fruit. For an ongoing bed, plan for at least 3 to 6 plants per person.',
    idealSetup: 'Mix one summer-bearing and one fall-bearing variety for harvests from early summer through first frost.',
    fallbackPlan: 'Any single raspberry variety is fully productive. Poor crops are almost always due to site, pruning, or pest issues rather than pollination.',
    varieties: [
      {
        id: 'prelude',
        name: 'Prelude',
        fruitType: 'raspberry',
        speciesGroup: 'red-yellow',
        speciesGroupLabel: 'Red or yellow raspberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'early-summer',
        bloomLabel: 'early summer',
        bloomOrder: 1,
        zoneRange: { min: 4, max: 8 },
        researchNotes: ['Earliest-ripening summer red; also produces a light fall primocane crop; ripens mid-June in mid-Atlantic regions'],
      },
      {
        id: 'boyne',
        name: 'Boyne',
        fruitType: 'raspberry',
        speciesGroup: 'red-yellow',
        speciesGroupLabel: 'Red or yellow raspberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'early-summer',
        bloomLabel: 'early summer',
        bloomOrder: 2,
        zoneRange: { min: 3, max: 8 },
        researchNotes: ['Extremely cold-hardy Manitoba introduction; small, dark, soft berries with excellent flavor; one of the best choices for zone 3 gardens'],
      },
      {
        id: 'killarney',
        name: 'Killarney',
        fruitType: 'raspberry',
        speciesGroup: 'red-yellow',
        speciesGroupLabel: 'Red or yellow raspberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'early-summer',
        bloomLabel: 'early summer',
        bloomOrder: 2,
        zoneRange: { min: 4, max: 8 },
        researchNotes: ['Bright, glossy, medium-firm berries; good disease resistance; ripens a week after Boyne, extending the early-summer window'],
      },
      {
        id: 'nova',
        name: 'Nova',
        fruitType: 'raspberry',
        speciesGroup: 'red-yellow',
        speciesGroupLabel: 'Red or yellow raspberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid-summer',
        bloomLabel: 'mid summer',
        bloomOrder: 3,
        zoneRange: { min: 3, max: 8 },
        researchNotes: ['Vigorous, nearly thornless mid-season summer bearer; reliable cold-hardy workhorse for zones 3 to 5'],
      },
      {
        id: 'latham',
        name: 'Latham',
        fruitType: 'raspberry',
        speciesGroup: 'red-yellow',
        speciesGroupLabel: 'Red or yellow raspberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid-summer',
        bloomLabel: 'mid summer',
        bloomOrder: 3,
        zoneRange: { min: 3, max: 8 },
        researchNotes: ['Heirloom mid-season variety; very hardy and tolerates poor soil; reliable workhorse for cold gardens'],
      },
      {
        id: 'josephine',
        name: 'Josephine',
        fruitType: 'raspberry',
        speciesGroup: 'red-yellow',
        speciesGroupLabel: 'Red or yellow raspberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid-summer',
        bloomLabel: 'mid summer',
        bloomOrder: 3,
        zoneRange: { min: 4, max: 8 },
        researchNotes: ['Large-fruited, productive mid-to-late summer bearer with excellent flavor'],
      },
      {
        id: 'autumn-bliss',
        name: 'Autumn Bliss',
        fruitType: 'raspberry',
        speciesGroup: 'red-yellow',
        speciesGroupLabel: 'Red or yellow raspberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'fall',
        bloomLabel: 'fall bearing',
        bloomOrder: 4,
        zoneRange: { min: 4, max: 9 },
        researchNotes: ['British-bred fall primocane; one of the earliest fall bearers, starting before Heritage; widely grown in the Pacific Northwest'],
      },
      {
        id: 'joan-j',
        name: 'Joan J',
        fruitType: 'raspberry',
        speciesGroup: 'red-yellow',
        speciesGroupLabel: 'Red or yellow raspberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'fall',
        bloomLabel: 'fall bearing',
        bloomOrder: 4,
        zoneRange: { min: 4, max: 8 },
        researchNotes: ['Spine-free canes; large, dark red berries; productive fall primocane; good for northern gardens'],
      },
      {
        id: 'heritage',
        name: 'Heritage',
        fruitType: 'raspberry',
        speciesGroup: 'red-yellow',
        speciesGroupLabel: 'Red or yellow raspberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'fall',
        bloomLabel: 'fall bearing',
        bloomOrder: 4,
        zoneRange: { min: 4, max: 8 },
        researchNotes: ['The standard fall-bearing red; heavy crops from late August through frost; slightly tart flavor holds well for fresh eating and jam'],
      },
      {
        id: 'caroline',
        name: 'Caroline',
        fruitType: 'raspberry',
        speciesGroup: 'red-yellow',
        speciesGroupLabel: 'Red or yellow raspberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'fall',
        bloomLabel: 'fall bearing',
        bloomOrder: 4,
        zoneRange: { min: 4, max: 9 },
        researchNotes: ['Improved Heritage type; larger, sweeter berries; starts slightly before Heritage; very productive fall primocane'],
      },
      {
        id: 'anne',
        name: 'Anne',
        fruitType: 'raspberry',
        speciesGroup: 'red-yellow',
        speciesGroupLabel: 'Red or yellow raspberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'fall',
        bloomLabel: 'fall bearing',
        bloomOrder: 4,
        zoneRange: { min: 4, max: 9 },
        researchNotes: ['Yellow-apricot primocane with distinctive mild sweet flavor; two crops per season; heat-tolerant'],
      },
      {
        id: 'fallgold',
        name: 'Fallgold',
        fruitType: 'raspberry',
        speciesGroup: 'red-yellow',
        speciesGroupLabel: 'Red or yellow raspberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'fall',
        bloomLabel: 'fall bearing',
        bloomOrder: 4,
        zoneRange: { min: 4, max: 8 },
        researchNotes: ['Yellow everbearing; very sweet; two crops per season; same plant sold as Fall Gold in many catalogs'],
      },
      {
        id: 'brandywine',
        name: 'Brandywine',
        fruitType: 'raspberry',
        speciesGroup: 'purple',
        speciesGroupLabel: 'Purple raspberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid-summer',
        bloomLabel: 'mid summer',
        bloomOrder: 3,
        zoneRange: { min: 4, max: 8 },
        researchNotes: ['Purple raspberry hybrid (red x black cross); very large, tart, richly flavored berries best suited for jam and cooking; summer-bearing floricane; compatible with both red and black varieties as a companion'],
      },
      {
        id: 'bristol',
        name: 'Bristol',
        fruitType: 'raspberry',
        speciesGroup: 'black',
        speciesGroupLabel: 'Black raspberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'early-summer',
        bloomLabel: 'early summer',
        bloomOrder: 2,
        zoneRange: { min: 4, max: 8 },
        researchNotes: ['Classic productive black raspberry; large, intensely flavored berries; fruit set improves noticeably with a second black variety nearby'],
      },
      {
        id: 'jewel',
        name: 'Jewel',
        fruitType: 'raspberry',
        speciesGroup: 'black',
        speciesGroupLabel: 'Black raspberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid-summer',
        bloomLabel: 'mid summer',
        bloomOrder: 3,
        zoneRange: { min: 4, max: 8 },
        researchNotes: ['Cornell-bred; large, glossy, intensely flavored berries; the most widely planted black raspberry; slightly later than Bristol'],
      },
      {
        id: 'allen',
        name: 'Allen',
        fruitType: 'raspberry',
        speciesGroup: 'black',
        speciesGroupLabel: 'Black raspberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid-summer',
        bloomLabel: 'mid summer',
        bloomOrder: 3,
        zoneRange: { min: 4, max: 8 },
        researchNotes: ['Compatible companion to Jewel and Bristol; good cross-pollinizer for other black varieties'],
      },
    ],
  },
  blackberry: {
    id: 'blackberry',
    label: 'Blackberry',
    description: 'All major blackberry varieties are self-fertile and produce full crops without a cross-pollinizer. The most useful planning dimension is fruiting type: floricane varieties fruit once in summer on second-year canes; Prime-Ark primocane varieties fruit in fall on first-year canes and again the following summer. Thornless erect varieties are the easiest to manage for most home gardens.',
    partnerDistanceNote: 'Blackberries are insect-pollinated and self-fertile, so a companion plant extends the harvest season rather than solving a pollination need.',
    minimumSetup: 'One plant will produce a full crop.',
    idealSetup: 'Mix one early-season and one late-season variety to spread the harvest over 6 to 8 weeks rather than concentrating it in 2 to 3 weeks.',
    fallbackPlan: 'Any single blackberry variety is fully productive. Poor crops are almost always a site, pruning, or cane disease issue.',
    varieties: [
      {
        id: 'natchez',
        name: 'Natchez',
        fruitType: 'blackberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'early-summer',
        bloomLabel: 'early summer',
        bloomOrder: 1,
        zoneRange: { min: 6, max: 10 },
        researchNotes: ['Very early season; large, elongated, sweet berries; thornless; heat-tolerant; best in zones 6 to 9'],
      },
      {
        id: 'arapaho',
        name: 'Arapaho',
        fruitType: 'blackberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'early-summer',
        bloomLabel: 'early summer',
        bloomOrder: 2,
        zoneRange: { min: 6, max: 9 },
        researchNotes: ['Erect, thornless; ripens before Apache and Navaho; medium-large, firm berries with good disease resistance'],
      },
      {
        id: 'ouachita',
        name: 'Ouachita',
        fruitType: 'blackberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid-summer',
        bloomLabel: 'mid summer',
        bloomOrder: 3,
        zoneRange: { min: 6, max: 9 },
        researchNotes: ['Productive erect thornless variety; large, high-quality berries with excellent post-harvest handling; white flowers'],
      },
      {
        id: 'apache',
        name: 'Apache',
        fruitType: 'blackberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid-summer',
        bloomLabel: 'mid summer',
        bloomOrder: 3,
        zoneRange: { min: 6, max: 9 },
        researchNotes: ['Erect, thornless; large, firm, excellent-flavored berries; pairs well with Ouachita for back-to-back mid-season harvest'],
      },
      {
        id: 'osage',
        name: 'Osage',
        fruitType: 'blackberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid-summer',
        bloomLabel: 'mid summer',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 9 },
        researchNotes: ['University of Arkansas release; erect, thornless; medium-large berries with good flavor; cold-hardier than most erect varieties'],
      },
      {
        id: 'triple-crown',
        name: 'Triple Crown',
        fruitType: 'blackberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid-late-summer',
        bloomLabel: 'mid-late summer',
        bloomOrder: 4,
        zoneRange: { min: 5, max: 9 },
        researchNotes: ['Semi-erect trailing type; large, sweet, flavorful berries; ripens between Ouachita and Chester; widely considered among the best-flavored thornless varieties'],
      },
      {
        id: 'navaho',
        name: 'Navaho',
        fruitType: 'blackberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid-late-summer',
        bloomLabel: 'mid-late summer',
        bloomOrder: 4,
        zoneRange: { min: 6, max: 9 },
        researchNotes: ['Erect, thornless; among the sweetest varieties; small, firm berries with long harvest window; excellent for southern gardens'],
      },
      {
        id: 'hull-thornless',
        name: 'Hull Thornless',
        fruitType: 'blackberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid-late-summer',
        bloomLabel: 'mid-late summer',
        bloomOrder: 4,
        zoneRange: { min: 5, max: 8 },
        researchNotes: ['Semi-erect trailing type; high yields; good for fresh eating and processing; ripens late June into August'],
      },
      {
        id: 'black-satin',
        name: 'Black Satin',
        fruitType: 'blackberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid-late-summer',
        bloomLabel: 'mid-late summer',
        bloomOrder: 4,
        zoneRange: { min: 6, max: 9 },
        researchNotes: ['Semi-erect thornless; good yields; moderately tart flavor; ripens July in most zones'],
      },
      {
        id: 'chester',
        name: 'Chester',
        fruitType: 'blackberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'late-summer',
        bloomLabel: 'late summer',
        bloomOrder: 5,
        zoneRange: { min: 5, max: 9 },
        researchNotes: ['Latest-season thornless variety; large, mild-sweet berries; excellent cold hardiness for a floricane; extends the season into August'],
      },
      {
        id: 'thornless-evergreen',
        name: 'Thornless Evergreen',
        fruitType: 'blackberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'late-summer',
        bloomLabel: 'late summer',
        bloomOrder: 5,
        zoneRange: { min: 6, max: 9 },
        researchNotes: ['Pacific Northwest trailing type; very high yields; late season; best suited to mild-winter West Coast climates'],
      },
      {
        id: 'prime-ark-freedom',
        name: 'Prime-Ark Freedom',
        fruitType: 'blackberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'fall',
        bloomLabel: 'fall bearing',
        bloomOrder: 4,
        zoneRange: { min: 5, max: 9 },
        researchNotes: ['First thornless primocane blackberry; bears a fall crop in year one, then a summer crop on the same canes year two; large, firm berries'],
      },
      {
        id: 'prime-ark-traveler',
        name: 'Prime-Ark Traveler',
        fruitType: 'blackberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'fall',
        bloomLabel: 'fall bearing',
        bloomOrder: 4,
        zoneRange: { min: 5, max: 9 },
        researchNotes: ['Second-generation thornless primocane; more reliable fall ripening in short-season areas than Prime-Ark Freedom'],
      },
    ],
  },
  strawberry: {
    id: 'strawberry',
    label: 'Strawberry',
    description: 'All strawberry varieties are self-fertile. The main planning distinction is crop type: June-bearing varieties produce one concentrated 2 to 3 week harvest in late spring or early summer; everbearing varieties give two main flushes (spring and fall); day-neutral varieties fruit continuously whenever temperatures stay between 35 and 85 degrees. All types require bee visitation for well-formed fruit - without pollinators, berries can be small and misshapen even if fruit sets.',
    partnerDistanceNote: 'Strawberries need pollinating insects. Keep a varied planting nearby to support bee activity throughout the fruiting season.',
    minimumSetup: 'One variety produces fully, but plan for at least 6 plants per person since individual plants yield modestly.',
    idealSetup: 'Mix one June-bearing variety for a concentrated harvest and one day-neutral for continuous production throughout the growing season.',
    fallbackPlan: 'Any single variety will produce fully. Deformed or seedy berries usually signal poor bee access, not a pollination partner problem.',
    varieties: [
      {
        id: 'earliglow',
        name: 'Earliglow',
        fruitType: 'strawberry',
        speciesGroup: 'june-bearing',
        speciesGroupLabel: 'June-bearing',
        selfFertility: 'self-fertile',
        bloomGroup: 'early-season',
        bloomLabel: 'early season',
        bloomOrder: 1,
        zoneRange: { min: 4, max: 8 },
        researchNotes: ['Earliest-ripening June-bearer; small to medium, intensely flavored berries; strong disease resistance; outstanding fresh flavor'],
      },
      {
        id: 'honeoye',
        name: 'Honeoye',
        fruitType: 'strawberry',
        speciesGroup: 'june-bearing',
        speciesGroupLabel: 'June-bearing',
        selfFertility: 'self-fertile',
        bloomGroup: 'early-season',
        bloomLabel: 'early season',
        bloomOrder: 2,
        zoneRange: { min: 3, max: 8 },
        researchNotes: ['Large, bright red, slightly tart berries; heavy yielder; widely adapted; ripens just after Earliglow'],
      },
      {
        id: 'cavendish',
        name: 'Cavendish',
        fruitType: 'strawberry',
        speciesGroup: 'june-bearing',
        speciesGroupLabel: 'June-bearing',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid-season',
        bloomLabel: 'mid season',
        bloomOrder: 3,
        zoneRange: { min: 3, max: 8 },
        researchNotes: ['Large, firm, sweet berries; excellent cold hardiness; consistent mid-season yields in northern gardens'],
      },
      {
        id: 'kent',
        name: 'Kent',
        fruitType: 'strawberry',
        speciesGroup: 'june-bearing',
        speciesGroupLabel: 'June-bearing',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid-season',
        bloomLabel: 'mid season',
        bloomOrder: 3,
        zoneRange: { min: 3, max: 8 },
        researchNotes: ['Large, dark red, firm berries; excellent cold hardiness; reliable mid-season producer for zones 3 to 5'],
      },
      {
        id: 'jewel-strawberry',
        name: 'Jewel',
        fruitType: 'strawberry',
        speciesGroup: 'june-bearing',
        speciesGroupLabel: 'June-bearing',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid-season',
        bloomLabel: 'mid season',
        bloomOrder: 3,
        zoneRange: { min: 3, max: 8 },
        researchNotes: ['Large, firm, classic strawberry flavor; widely grown in the northeast; productive and disease-tolerant'],
      },
      {
        id: 'chandler',
        name: 'Chandler',
        fruitType: 'strawberry',
        speciesGroup: 'june-bearing',
        speciesGroupLabel: 'June-bearing',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid-season',
        bloomLabel: 'mid season',
        bloomOrder: 3,
        zoneRange: { min: 4, max: 8 },
        researchNotes: ['Large, flavorful berries beloved by California growers; best in zones 6 to 8; mid-season but sensitive to cold winters'],
      },
      {
        id: 'allstar',
        name: 'Allstar',
        fruitType: 'strawberry',
        speciesGroup: 'june-bearing',
        speciesGroupLabel: 'June-bearing',
        selfFertility: 'self-fertile',
        bloomGroup: 'late-season',
        bloomLabel: 'late season',
        bloomOrder: 4,
        zoneRange: { min: 3, max: 8 },
        researchNotes: ['Large, mild, sweet berries; good disease resistance; ripens toward the end of the June-bearing window; very cold-hardy'],
      },
      {
        id: 'sparkle',
        name: 'Sparkle',
        fruitType: 'strawberry',
        speciesGroup: 'june-bearing',
        speciesGroupLabel: 'June-bearing',
        selfFertility: 'self-fertile',
        bloomGroup: 'late-season',
        bloomLabel: 'late season',
        bloomOrder: 5,
        zoneRange: { min: 3, max: 8 },
        researchNotes: ['Heirloom late-season variety; soft, rich-flavored berries excellent for jam; among the last June-bearing varieties to ripen'],
      },
      {
        id: 'albion',
        name: 'Albion',
        fruitType: 'strawberry',
        speciesGroup: 'day-neutral',
        speciesGroupLabel: 'Day-neutral',
        selfFertility: 'self-fertile',
        bloomGroup: 'continuous',
        bloomLabel: 'continuous production',
        bloomOrder: 3,
        zoneRange: { min: 4, max: 8 },
        researchNotes: ['Very large, sweet, firm berries with exceptional flavor; fruits spring through fall; best in zones 6 to 8 where summers are warm'],
      },
      {
        id: 'seascape',
        name: 'Seascape',
        fruitType: 'strawberry',
        speciesGroup: 'day-neutral',
        speciesGroupLabel: 'Day-neutral',
        selfFertility: 'self-fertile',
        bloomGroup: 'continuous',
        bloomLabel: 'continuous production',
        bloomOrder: 3,
        zoneRange: { min: 4, max: 9 },
        researchNotes: ['Large, firm, flavorful berries; heat-tolerant; continuous production; good in the Pacific Northwest and warm climates'],
      },
      {
        id: 'evie-2',
        name: 'Evie-2',
        fruitType: 'strawberry',
        speciesGroup: 'day-neutral',
        speciesGroupLabel: 'Day-neutral',
        selfFertility: 'self-fertile',
        bloomGroup: 'continuous',
        bloomLabel: 'continuous production',
        bloomOrder: 3,
        zoneRange: { min: 4, max: 8 },
        researchNotes: ['Very high-yielding day-neutral; less sensitive to summer heat than Albion; good pick for zones 4 to 6'],
      },
      {
        id: 'ozark-beauty',
        name: 'Ozark Beauty',
        fruitType: 'strawberry',
        speciesGroup: 'everbearing',
        speciesGroupLabel: 'Everbearing',
        selfFertility: 'self-fertile',
        bloomGroup: 'continuous',
        bloomLabel: 'spring and fall flushes',
        bloomOrder: 3,
        zoneRange: { min: 3, max: 9 },
        researchNotes: ['Classic everbearing (two main flushes, not continuous); large, sweet berries; very cold-tolerant; one of the most widely grown everbearers'],
      },
      {
        id: 'quinault',
        name: 'Quinault',
        fruitType: 'strawberry',
        speciesGroup: 'everbearing',
        speciesGroupLabel: 'Everbearing',
        selfFertility: 'self-fertile',
        bloomGroup: 'continuous',
        bloomLabel: 'spring and fall flushes',
        bloomOrder: 3,
        zoneRange: { min: 4, max: 8 },
        researchNotes: ['Everbearing with two main crops; soft, sweet, large berries; good fresh-eating variety'],
      },
    ],
  },
  aronia: {
    id: 'aronia',
    label: 'Aronia',
    description: 'All aronia (chokeberry) varieties are self-fertile and will produce fruit solo, but cross-pollination between two different varieties consistently improves fruit set, berry size, and total yield. All commercially available varieties bloom at the same time in mid-spring, so any two-variety combination will cross-pollinate effectively. Most major cultivars (Viking, Nero, McKenzie) are x Sorbaronia mitschurinii hybrids rather than pure Aronia melanocarpa, though they behave identically for all garden purposes.',
    partnerDistanceNote: 'Plant aronia companions in the same bed or nearby. Bees readily move between plants during the mid-spring bloom.',
    minimumSetup: 'One plant will fruit. Adding a second variety is the single most effective step for a heavier harvest.',
    idealSetup: 'Two to three varieties of varying habit (upright and compact) to maximize pollinator movement and total yield.',
    fallbackPlan: 'A solo aronia is productive enough for most uses. Add a second variety only if yield is the priority.',
    varieties: [
      {
        id: 'viking',
        name: 'Viking',
        fruitType: 'aronia',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid-spring',
        bloomLabel: 'mid-spring bloom',
        bloomOrder: 3,
        zoneRange: { min: 3, max: 9 },
        researchNotes: ['Most widely planted variety worldwide; vigorous 5 to 6 ft shrub; heavy cropper with classic tart-astringent flavor'],
      },
      {
        id: 'nero',
        name: 'Nero',
        fruitType: 'aronia',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid-spring',
        bloomLabel: 'mid-spring bloom',
        bloomOrder: 3,
        zoneRange: { min: 3, max: 9 },
        researchNotes: ['Similar to Viking in yield and flavor; slightly less vigorous; good companion and cross-pollinator for Viking'],
      },
      {
        id: 'mckenzie',
        name: 'McKenzie',
        fruitType: 'aronia',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid-spring',
        bloomLabel: 'mid-spring bloom',
        bloomOrder: 3,
        zoneRange: { min: 3, max: 8 },
        researchNotes: ['Largest-growing cultivar (9 to 12 ft); believed to be closer to pure A. melanocarpa than Viking or Nero'],
      },
      {
        id: 'autumn-magic',
        name: 'Autumn Magic',
        fruitType: 'aronia',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid-spring',
        bloomLabel: 'mid-spring bloom',
        bloomOrder: 3,
        zoneRange: { min: 3, max: 8 },
        researchNotes: ['University of British Columbia selection; confirmed true A. melanocarpa; excellent fall color; similar yield to Viking'],
      },
      {
        id: 'iroquois-beauty',
        name: 'Iroquois Beauty',
        aliases: ['Morton'],
        fruitType: 'aronia',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid-spring',
        bloomLabel: 'mid-spring bloom',
        bloomOrder: 3,
        zoneRange: { min: 3, max: 8 },
        researchNotes: ['Morton Arboretum selection; compact habit (2 to 3 ft tall, 4 to 5 ft wide); same self-fertile behavior; good for smaller spaces'],
      },
      {
        id: 'low-scape-mound',
        name: 'Low Scape Mound',
        fruitType: 'aronia',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid-spring',
        bloomLabel: 'mid-spring bloom',
        bloomOrder: 3,
        zoneRange: { min: 3, max: 9 },
        researchNotes: ['Very compact (18 to 24 inches); Proven Winners introduction; self-fertile; same mid-spring bloom; fruits but with lower yield than upright types'],
      },
    ],
  },
  'goji-berry': {
    id: 'goji-berry',
    label: 'Goji Berry',
    description: 'All goji berry (Lycium barbarum) varieties are self-fertile. Cross-pollination between two plants - same or different variety - measurably increases yield, with some growers reporting dramatically larger harvests. Goji blooms mid-summer over a long window, so any two plants will easily cross-pollinate. Most named varieties are hardy to zones 4 to 9, with Phoenix Tears the notable exception at zone 3.',
    partnerDistanceNote: 'Goji flowers are pollinated by bees. Two plants in the same bed or within 20 feet will readily cross-pollinate during the long summer bloom.',
    minimumSetup: 'One plant will fruit. A second plant noticeably increases yield.',
    idealSetup: 'Two or more plants of different varieties for cross-pollination and improved yields through the long summer bloom window.',
    fallbackPlan: 'A single goji plant will produce a usable crop. Poor yields from a solo plant are improved by adding a companion rather than fertilizing more.',
    varieties: [
      {
        id: 'phoenix-tears',
        name: 'Phoenix Tears',
        fruitType: 'goji-berry',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid-summer',
        bloomLabel: 'mid-summer bloom',
        bloomOrder: 3,
        zoneRange: { min: 3, max: 9 },
        researchNotes: ['Most cold-hardy named cultivar; compact, trellis-friendly habit; deep orange, jellybean-shaped fruits; best choice for zones 3 to 4'],
      },
      {
        id: 'big-lifeberry',
        name: 'Big Lifeberry',
        fruitType: 'goji-berry',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid-summer',
        bloomLabel: 'mid-summer bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 9 },
        researchNotes: ['University of Minnesota introduction; larger, juicier berries than the species; selected for fresh eating quality'],
      },
      {
        id: 'sweet-lifeberry',
        name: 'Sweet Lifeberry',
        fruitType: 'goji-berry',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid-summer',
        bloomLabel: 'mid-summer bloom',
        bloomOrder: 3,
        zoneRange: { min: 5, max: 9 },
        researchNotes: ['University of Minnesota introduction; very sweet, slightly tangy ripe berries; vigorous to 10 to 12 ft unpruned; good companion for Big Lifeberry'],
      },
      {
        id: 'crimson-star',
        name: 'Crimson Star',
        fruitType: 'goji-berry',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid-summer',
        bloomLabel: 'mid-summer bloom',
        bloomOrder: 3,
        zoneRange: { min: 4, max: 9 },
        researchNotes: ['Medium 6 to 8 ft plant; large, bright red berries with tangy sweetness; bears 1 to 2 years after planting'],
      },
      {
        id: 'ningxia',
        name: 'Ningxia',
        fruitType: 'goji-berry',
        selfFertility: 'self-fertile',
        bloomGroup: 'mid-summer',
        bloomLabel: 'mid-summer bloom',
        bloomOrder: 3,
        zoneRange: { min: 4, max: 9 },
        researchNotes: ['Traditional Chinese cultivar; classic wolfberry type; berries better suited to drying than fresh eating'],
      },
    ],
  },
  cranberry: {
    id: 'cranberry',
    label: 'Cranberry',
    description: 'Cranberry is functionally cross-pollination-dependent - solo plants set less than 5% of their flowers as fruit under garden conditions. Two genetically distinct varieties planted within 10 to 15 feet of each other are required for any meaningful harvest. Bumblebees are significantly more effective pollinators than honeybees for this species because of the vibrational (buzz) pollination the flowers require. All varieties also need highly acidic soil (pH 4.0 to 5.5) and consistent moisture.',
    partnerDistanceNote: 'Plant cranberry varieties within 10 to 15 feet for reliable bumblebee movement between them. Closer is better in sheltered or low-pollinator sites.',
    minimumSetup: 'Two genetically distinct varieties are required. A solo planting will produce very little fruit regardless of care.',
    idealSetup: 'Three varieties covering early, mid, and late bloom so bumblebees stay active in the bed throughout the full flowering window.',
    fallbackPlan: 'If a solo planting produces almost no fruit, adding any second cranberry variety is the fix - cross-pollination is the limiting factor.',
    varieties: [
      {
        id: 'ben-lear',
        name: 'Ben Lear',
        fruitType: 'cranberry',
        selfFertility: 'partially-self-fertile',
        bloomGroup: 'early',
        bloomLabel: 'early bloom',
        bloomOrder: 1,
        zoneRange: { min: 2, max: 5 },
        researchNotes: ['Very productive early variety; large, deep red fruit; heavy cropper; selected 1901; pairs well with Stevens or Howes'],
      },
      {
        id: 'early-black',
        name: 'Early Black',
        fruitType: 'cranberry',
        selfFertility: 'partially-self-fertile',
        bloomGroup: 'early',
        bloomLabel: 'early bloom',
        bloomOrder: 1,
        zoneRange: { min: 4, max: 7 },
        researchNotes: ['Earliest-ripening variety; compact trailing habit; slightly higher pH tolerance than most cranberries; heirloom "Big Four" variety'],
      },
      {
        id: 'pilgrim',
        name: 'Pilgrim',
        fruitType: 'cranberry',
        selfFertility: 'partially-self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 3, max: 7 },
        researchNotes: ['USDA-bred hybrid; false-blossom resistant and frost-tolerant; juicy, low-pectin fruit; pairs with Stevens or Ben Lear'],
      },
      {
        id: 'stevens',
        name: 'Stevens',
        fruitType: 'cranberry',
        selfFertility: 'partially-self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 3, max: 7 },
        researchNotes: ['Most widely planted commercial variety; large, consistent crops; sets 3 to 5 times more fruit with a cross-pollinator than alone'],
      },
      {
        id: 'searles',
        name: 'Searles',
        fruitType: 'cranberry',
        selfFertility: 'partially-self-fertile',
        bloomGroup: 'mid',
        bloomLabel: 'mid bloom',
        bloomOrder: 3,
        zoneRange: { min: 3, max: 6 },
        researchNotes: ['Heirloom "Big Four" discovery (1893); reliable mid-season producer; widely used in Wisconsin commercial operations'],
      },
      {
        id: 'mcfarlin',
        name: 'McFarlin',
        fruitType: 'cranberry',
        selfFertility: 'partially-self-fertile',
        bloomGroup: 'mid-late',
        bloomLabel: 'mid-late bloom',
        bloomOrder: 4,
        zoneRange: { min: 5, max: 6 },
        researchNotes: ['Heirloom "Big Four"; disease-resistant; vigorous; sets fruit reliably with one companion; best suited to zones 5 to 6'],
      },
      {
        id: 'howes',
        name: 'Howes',
        fruitType: 'cranberry',
        selfFertility: 'partially-self-fertile',
        bloomGroup: 'late',
        bloomLabel: 'late bloom',
        bloomOrder: 5,
        zoneRange: { min: 3, max: 7 },
        researchNotes: ['Latest-ripening heirloom; frost-resistant; shiny, firm berries; more self-fertile than most varieties but still benefits strongly from a cross-pollinator'],
      },
    ],
  },
  lingonberry: {
    id: 'lingonberry',
    label: 'Lingonberry',
    description: 'Lingonberry is self-fertile but consistently sets larger berries that ripen earlier when a cross-pollinator is present. Extension research documents that designated pollinizer varieties (Red Pearl, Sussi) significantly boost the yield of high-producing varieties when interplanted at roughly a 1:5 to 1:10 ratio. Most varieties bloom twice - a spring flush and a summer flush - producing two fruit crops per year. Lingonberry requires acidic soil (pH 4.5 to 5.5), similar to blueberry.',
    partnerDistanceNote: 'Interplant pollinizer varieties at one plant per 5 to 10 of the main variety. Standard mixed-bed planting works well since lingonberry plants are small.',
    minimumSetup: 'One variety will fruit, but yields and berry size are noticeably lower than with a companion.',
    idealSetup: 'A main fruiting variety (Koralle or Linnaea) with Red Pearl as a pollinizer at a 1:5 to 1:10 ratio.',
    fallbackPlan: 'A solo lingonberry bed still produces fruit. Adding Red Pearl is the most cost-effective improvement for yield and berry size.',
    varieties: [
      {
        id: 'koralle',
        name: 'Koralle',
        fruitType: 'lingonberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'spring-and-fall',
        bloomLabel: 'spring and fall bloom',
        bloomOrder: 3,
        zoneRange: { min: 2, max: 8 },
        researchNotes: ['Dominant European commercial variety; compact and upright; two crops per year; small, tart, highly flavored fruit; benefits most from Red Pearl as a companion'],
      },
      {
        id: 'red-pearl',
        name: 'Red Pearl',
        fruitType: 'lingonberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'spring-and-fall',
        bloomLabel: 'spring and fall bloom',
        bloomOrder: 2,
        zoneRange: { min: 3, max: 9 },
        researchNotes: ['Designated pollinizer cultivar; lower-yielding on its own but boosts yield of Koralle and others significantly; larger, milder fruit; plant 1 Red Pearl per 5 to 10 plants of other varieties'],
      },
      {
        id: 'sussi',
        name: 'Sussi',
        fruitType: 'lingonberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'spring-and-fall',
        bloomLabel: 'spring and fall bloom',
        bloomOrder: 3,
        zoneRange: { min: 3, max: 7 },
        researchNotes: ['Swedish pollinizer cultivar; slow to establish but eventually a heavy producer; spreading habit 7 to 10 inches tall; useful as a secondary pollinizer alongside Red Pearl'],
      },
      {
        id: 'linnaea',
        name: 'Linnaea',
        fruitType: 'lingonberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'spring-and-fall',
        bloomLabel: 'spring and fall bloom',
        bloomOrder: 3,
        zoneRange: { min: 2, max: 6 },
        researchNotes: ['Swedish variety; vigorous and upright; high yield potential; smaller berries than Red Pearl; best for colder zones 2 to 6'],
      },
      {
        id: 'erntedank',
        name: 'Erntedank',
        fruitType: 'lingonberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'spring-and-fall',
        bloomLabel: 'spring and fall bloom',
        bloomOrder: 3,
        zoneRange: { min: 3, max: 7 },
        researchNotes: ['German high-yielder; 14 to 16 inches tall; consistent spring and fall crops; small to medium bright red berries; good commercial variety'],
      },
      {
        id: 'ida',
        name: 'Ida',
        fruitType: 'lingonberry',
        selfFertility: 'self-fertile',
        bloomGroup: 'spring-and-fall',
        bloomLabel: 'spring and fall bloom',
        bloomOrder: 4,
        zoneRange: { min: 3, max: 7 },
        researchNotes: ['Swedish variety; upright habit; notably large berries for a lingonberry; good fresh-eating quality; pairs well with Red Pearl'],
      },
    ],
  },
};
